import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Gamepad,
  Download,
  Upload,
  Github,
  RefreshCw,
  Wrench,
  Image as ImageIcon,
  X,
  Terminal as TerminalIcon,
} from 'lucide-react';
// Use Gamepad (not Gamepad2) for better compatibility in preview iframes
const GamepadIcon = Gamepad;
import { LivePreviewToolbar, DeviceSize } from './LivePreviewToolbar';
import { Button } from './Button';
import { PublishSiteDialog } from './PublishSiteDialog';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { useUpdateFile } from '../helpers/useFilesApi';
import { useOptionalAlternativeRuntime, RuntimeType } from '../helpers/AlternativeRuntimeProvider';
import { useVibePreview } from '../hooks/useVibePreview';
import { publishRustPreview } from '../lib/vibesPreview';
import { toast } from 'sonner';
import { normalizePath } from '../helpers/normalizePath';
import { needsEsbuildBundling } from '../lib/esbuildBundler';
import { startWebContainerPreview, teardownWebContainer, onWebContainerError, spawnInWebContainer, injectThemeIntoWebContainer } from '../lib/webcontainerPreview';
import { PREVIEW_NAV_GUARD_SCRIPT } from '../lib/previewGuardScript';
import { TAILWIND_DESIGN_BASELINE, FONT_BASELINE } from '../lib/esbuildHtmlAssembler';
import { PREVIEW_INSPECTOR_SCRIPT } from '../lib/previewInspectorScript';
import html2canvas from 'html2canvas';
import { AnnotationCanvas } from './AnnotationCanvas';
import { LivePreviewTerminal } from './LivePreviewTerminal';
import styles from './LivePreview.module.css';
import { safeCap } from '../helpers/stringUtils';
import { useQueryClient } from '../lib/queryClient';
import { autoFixComponent } from '../endpoints/ai/autofix_POST.schema';
import { createFile } from '../endpoints/files/create_POST.schema';
import { resolveUnknownImports } from '../lib/importResolver';
import { getCustomThemeVars, useThemeMode } from '../helpers/themeMode';
import { getShadcnBundledFiles } from '../helpers/shadcnBundle';
import { useThemes } from '../helpers/useThemesApi';

// Module-level autofix tracking — survives component unmount/remount cycles.
// Keyed by "sessionId:componentName" to avoid cross-session leaks.
const _autofixAttempts = new Map<string, number>();
const _autofixInFlight = new Set<string>();
import type { ThemeTokens } from '../endpoints/themes/create_POST.schema';

// Godot Web Editor URL - hosted on R2 at gdot.tasking.tech
const GODOT_WEB_EDITOR_URL =
  import.meta.env.VITE_GODOT_EDITOR_URL || 'https://gdot.tasking.tech/godot.editor.html';
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico', 'bmp', 'avif'] as const;

const SAFE_HEADER_COMPONENT = `import * as React from "react";

export interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export function Header({ title = "Tasking.tech", subtitle = "Live Preview" }: HeaderProps) {
  return (
    <header className="w-full border-b border-border bg-background/95 px-4 py-3">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold">{title}</h1>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    </header>
  );
}

export default Header;`;

// Disable preview autofix so live preview surfaces real build failures instead of mutating session files.
const ENABLE_PREVIEW_AUTOFIX = false;
// Disabled by request: this fallback can mask broken session builds by showing only an asset/logo.
const ENABLE_MEDIA_ONLY_FALLBACK = false;
// Disabled by request: branded starter splash can also mask actual preview output.
const ENABLE_STARTER_SPLASH = false;
const PREVIEW_INSPECTOR_SCRIPT_CONTENT = PREVIEW_INSPECTOR_SCRIPT
  .replace(/^<script>\s*/i, '')
  .replace(/\s*<\/script>\s*$/i, '');

function normalizePreviewErrorText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || String(value);
  if (value == null) return '';
  if (typeof value === 'object') {
    const maybeMessage = (value as any).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
    const maybeText = (value as any).text;
    if (typeof maybeText === 'string' && maybeText.trim()) return maybeText;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function normalizePreviewHtml(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? value : null;
  }
  if (value == null) return null;
  if (value instanceof Error) {
    const msg = value.message?.trim();
    return msg ? `<pre>${msg.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>` : null;
  }
  if (typeof value === 'object') {
    const maybeHtml = (value as any).html;
    if (typeof maybeHtml === 'string' && maybeHtml.trim()) return maybeHtml;
    const maybeContent = (value as any).content;
    if (typeof maybeContent === 'string' && maybeContent.trim()) return maybeContent;
    try {
      const json = JSON.stringify(value, null, 2);
      return json ? `<pre>${json.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>` : null;
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function escapePreviewStyleText(value: string): string {
  return value.replace(/<\/style/gi, '<\\/style');
}

function sanitizeThemeCssValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const sanitized = value.replace(/[\r\n]+/g, ' ').replace(/[<>]/g, '').trim();
  return sanitized || null;
}

function buildPreviewThemeBridge(
  effectiveTheme: 'light' | 'dark',
  customThemeVars: Record<string, string> | null,
): string {
  const cssVarLines = Object.entries(customThemeVars || {})
    .map(([key, value]) => {
      if (!key.startsWith('--')) return null;
      const sanitizedValue = sanitizeThemeCssValue(value);
      if (!sanitizedValue) return null;
      return `  ${key}: ${sanitizedValue};`;
    })
    .filter((line): line is string => Boolean(line));

  const styleCss = [
    ':root {',
    `  color-scheme: ${effectiveTheme};`,
    ...cssVarLines,
    '}',
    `html, body { color-scheme: ${effectiveTheme}; }`,
  ].join('\n');

  const script = `<script data-tasking-preview-theme>(function(){
  var theme=${JSON.stringify(effectiveTheme)};
  function applyTheme(){
    var root=document.documentElement;
    var body=document.body;
    if(root){
      root.classList.remove('light','dark');
      root.classList.add(theme);
      root.setAttribute('data-theme', theme);
      root.style.colorScheme = theme;
    }
    if(body){
      body.classList.remove('light','dark');
      body.classList.add(theme);
      body.setAttribute('data-theme', theme);
      body.style.colorScheme = theme;
    }
  }
  applyTheme();
  if(document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyTheme, { once: true });
  }
})();</script>`;

  return `${script}\n<style data-tasking-preview-theme-vars>\n${escapePreviewStyleText(styleCss)}\n</style>`;
}

// Token-to-CSS-variable mapping (mirrors ThemesPanel TOKEN_TO_APP_VAR_MAP)
const SESSION_THEME_TOKEN_MAP: Record<string, string[]> = {
  primary: ['--primary'],
  secondary: ['--secondary'],
  accent: ['--accent'],
  background: ['--background'],
  surface: ['--surface', '--card', '--sidebar', '--popup', '--muted'],
  text: ['--foreground', '--surface-foreground', '--card-foreground', '--sidebar-foreground', '--popup-foreground'],
  textMuted: ['--muted-foreground'],
  border: ['--border'],
  error: ['--error', '--destructive'],
  success: ['--success'],
  warning: ['--warning'],
};

/** Build a <style> tag from session theme tokens to inject into preview */
function buildSessionThemeStyle(tokens: ThemeTokens, darkTokens?: ThemeTokens): string {
  const emitVars = (t: ThemeTokens): string[] => {
    const lines: string[] = [];
    if (t.colors) {
      for (const [key, value] of Object.entries(t.colors)) {
        const mapped = SESSION_THEME_TOKEN_MAP[key];
        if (mapped) mapped.forEach((v) => lines.push(`${v}: ${value};`));
      }
    }
    if (t.borderRadius) {
      if (t.borderRadius.sm) lines.push(`--radius-sm: ${t.borderRadius.sm};`);
      if (t.borderRadius.md) lines.push(`--radius: ${t.borderRadius.md};`);
      if (t.borderRadius.lg) lines.push(`--radius-lg: ${t.borderRadius.lg};`);
    }
    if (t.shadows) {
      if (t.shadows.sm) lines.push(`--shadow: ${t.shadows.sm};`);
      if (t.shadows.md) lines.push(`--shadow-md: ${t.shadows.md};`);
      if (t.shadows.lg) lines.push(`--shadow-lg: ${t.shadows.lg};`);
    }
    return lines;
  };

  const rootVars = emitVars(tokens);
  let css = `:root, .light {\n  ${rootVars.join('\n  ')}\n}`;
  if (darkTokens) {
    const darkVars = emitVars(darkTokens);
    css += `\n.dark {\n  ${darkVars.join('\n  ')}\n}`;
  } else {
    // Apply same tokens to dark mode too so they override the baseline
    css += `\n.dark {\n  ${rootVars.join('\n  ')}\n}`;
  }
  return `<style data-tasking-session-theme>\n${escapePreviewStyleText(css)}\n</style>`;
}

// Helper to download all files as a ZIP
async function downloadFilesAsZip(
  files: Selectable<SessionFiles>[],
  projectName: string = 'project',
) {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  files.forEach((file) => {
    const fullPath =
      file.filePath === '/' || file.filePath === '' || !file.filePath
        ? file.fileName
        : `${file.filePath.replace(/^\//, '').replace(/\/$/, '')}/${file.fileName}`;
    zip.file(fullPath, file.content || '');
  });

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getAssetMimeType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'avif') return 'image/avif';
  if (ext === 'bmp') return 'image/bmp';
  if (ext === 'ico') return 'image/x-icon';
  return 'application/octet-stream';
}

function buildInspectContext(el: any): string {
  const parts: (string | null)[] = [
    `I clicked on this element in the live preview:`,
    `• Tag: <${el.tag}${el.id ? ` id="${el.id}"` : ''}${el.classes ? ` class="${el.classes}"` : ''}>`,
    el.text ? `• Text content: "${el.text}"` : null,
    `• CSS path: ${el.path}`,
    `• Size: ${Math.round(el.rect.width)}×${Math.round(el.rect.height)}px`,
    `• Styles: color=${el.styles.color}, background=${el.styles.backgroundColor}, fontSize=${el.styles.fontSize}, padding=${el.styles.padding}, margin=${el.styles.margin}`,
    el.styles.backgroundImage && el.styles.backgroundImage !== 'none'
      ? `• Background image: ${el.styles.backgroundImage}`
      : null,
    ``,
    `I want to: `,
  ];
  return parts.filter(Boolean).join('\n');
}

interface LivePreviewProps {
  code: string;
  language: string;
  sessionId: string;
  sessionFiles?: Selectable<SessionFiles>[];
  templateId?: string | null;
  onScreenshot?: (dataUrl: string) => void;
  onAnnotate?: (dataUrl: string, textNotes?: string[]) => void;
  className?: string;
  selectedPage?: string;
  onPageChange?: (page: string) => void;
  onImportFiles?: () => void;
  onImportFromGitHub?: () => void;
  onTryFix?: (errorContext: string) => void;
  onOpenPackageManager?: () => void;
  onElementEditApply?: (filePath: string, newContent: string) => Promise<void>;
  sessionName?: string;
  isInspectMode?: boolean;
  onInspect?: () => void;
  screenshotTriggerRef?: React.MutableRefObject<(() => void) | null>;
}

export const LivePreview: React.FC<LivePreviewProps> = ({
  code,
  language,
  sessionId,
  sessionFiles = [],
  templateId,
  onScreenshot,
  onAnnotate,
  className,
  selectedPage,
  onPageChange,
  onImportFiles,
  onImportFromGitHub,
  sessionName,
  onTryFix,
  onOpenPackageManager,
  onElementEditApply,
  isInspectMode: externalInspectMode,
  onInspect,
  screenshotTriggerRef,
}) => {
  const queryClient = useQueryClient();
  const { effectiveTheme, mode, customThemeId } = useThemeMode();
  const previewCustomThemeVars = useMemo(() => getCustomThemeVars(), [mode, customThemeId]);

  // Fetch active theme to inject into preview (global themes — session themes are legacy)
  const { data: sessionThemesData } = useThemes(null);
  const activeSessionThemeStyle = useMemo(() => {
    const themes = sessionThemesData?.themes;
    if (!themes || themes.length === 0) return '';
    const active = themes.find((t) => t.isActive);
    if (!active?.tokens) return '';
    try {
      const tokens = typeof active.tokens === 'string' ? JSON.parse(active.tokens) as ThemeTokens : active.tokens as unknown as ThemeTokens;
      // Check for paired dark theme
      let darkTokens: ThemeTokens | undefined;
      const pairedId = (active as any).pairedThemeId;
      if (pairedId) {
        const paired = themes.find((t) => t.id === pairedId);
        if (paired?.tokens) {
          darkTokens = typeof paired.tokens === 'string' ? JSON.parse(paired.tokens) as ThemeTokens : paired.tokens as unknown as ThemeTokens;
        }
      }
      return buildSessionThemeStyle(tokens, darkTokens);
    } catch {
      return '';
    }
  }, [sessionThemesData]);

  // Build plain CSS from active theme tokens for WebContainer injection
  const activeThemeCss = useMemo(() => {
    const themes = sessionThemesData?.themes;
    if (!themes || themes.length === 0) return '';
    const active = themes.find((t) => t.isActive);
    if (!active?.tokens) return '';
    try {
      const tokens = typeof active.tokens === 'string' ? JSON.parse(active.tokens) as ThemeTokens : active.tokens as unknown as ThemeTokens;
      const lines: string[] = [];
      if (tokens.colors) {
        for (const [key, value] of Object.entries(tokens.colors)) {
          const mapped = SESSION_THEME_TOKEN_MAP[key];
          if (mapped) mapped.forEach((v) => lines.push(`${v}: ${value};`));
        }
      }
      return lines.length > 0 ? `:root {\n  ${lines.join('\n  ')}\n}` : '';
    } catch { return ''; }
  }, [sessionThemesData]);

  const [activePreviewTab, setActivePreviewTab] = useState<'preview' | 'assets' | 'terminal'>('preview');
  const [assetLightbox, setAssetLightbox] = useState<Selectable<SessionFiles> | null>(null);
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const [internalSelectedPage, setInternalSelectedPage] = useState<string | undefined>(
    selectedPage,
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewAreaRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const iframeLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [runtimeErrors, setRuntimeErrors] = useState<string[]>([]);
  const [previewReadySignal, setPreviewReadySignal] = useState(false);
  const previewReadySignalRef = useRef(false);
  const lastRuntimeErrorMessageRef = useRef<string | null>(null);
  const srcDocHealthTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const srcDocDomProbeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── Silent auto-fix: generate missing components via AI without user interaction ──
  // Uses module-level Maps (_autofixAttempts, _autofixInFlight) so counters survive
  // component unmount/remount cycles caused by parent re-renders after file saves.
  const autoFixMaxRetries = 2;
  // Ref to decouple message handler from sessionFiles dependency (prevents infinite re-render loop)
  const sessionFilesRef = useRef(sessionFiles);
  sessionFilesRef.current = sessionFiles;
  // Cooldown: prevent autofix query invalidation from cascading into infinite rebuild loops
  const lastAutofixInvalidationRef = useRef(0);

  // Cleanup timers on unmount — keep WebContainer alive as singleton
  // so switching tabs doesn't trigger a full rebuild
  useEffect(() => {
    return () => {
      if (iframeLoadTimeoutRef.current) clearTimeout(iframeLoadTimeoutRef.current);
      if (srcDocHealthTimeoutRef.current) clearTimeout(srcDocHealthTimeoutRef.current);
      if (srcDocDomProbeTimeoutRef.current) clearTimeout(srcDocDomProbeTimeoutRef.current);
      if (buildTimerRef.current) clearTimeout(buildTimerRef.current);
      if (webContainerTimerRef.current) clearTimeout(webContainerTimerRef.current);
      if (publishTimerRef.current) clearTimeout(publishTimerRef.current);
    };
  }, []);

  // Listen for reload preview event
  useEffect(() => {
    const handleReloadPreview = () => {
      setRefreshKey((k) => k + 1);
      setIframeLoading(true);
    };
    window.addEventListener('tasking.reloadPreview', handleReloadPreview);
    return () => window.removeEventListener('tasking.reloadPreview', handleReloadPreview);
  }, []);

  const [isCapturing, setIsCapturing] = useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  // Inline annotation mode (snipping-tool overlay)
  const [isAnnotatingInline, setIsAnnotatingInline] = useState(false);
  const [annotationScreenshot, setAnnotationScreenshot] = useState<string | null>(null);
  const [showGodotEditor, setShowGodotEditor] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInspectMode, setIsInspectMode] = useState(false);
  const [starterSplashExpired, setStarterSplashExpired] = useState(false);
  const effectiveInspectMode = externalInspectMode ?? isInspectMode;

  const setInspectMode = useCallback(
    (next: boolean) => {
      if (externalInspectMode === undefined) {
        setIsInspectMode(next);
        return;
      }
      if (onInspect && externalInspectMode !== next) {
        onInspect();
      }
    },
    [externalInspectMode, onInspect],
  );

  // Element editing: track pending changes with confirm/redo/undo
  const updateFileMutation = useUpdateFile();
  const [elementChange, setElementChange] = useState<{
    element: any;
    action: 'upload' | 'regenerate';
    imageData?: string;
    generatedCode?: string;
    originalFile?: Selectable<SessionFiles>;
    originalContent?: string;
    isApplying?: boolean;
  } | null>(null);

  // esbuild build state
  const [builtSrcDoc, setBuiltSrcDoc] = useState<string | null>(null);
  const [buildErrors, setBuildErrors] = useState<string[]>([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const buildTimerRef = useRef<number | null>(null);
  const buildTimeoutRef = useRef<number | null>(null);

  // WebContainer preview state
  const [webContainerUrl, setWebContainerUrl] = useState<string | null>(null);
  const webContainerTimerRef = useRef<number | null>(null);

  // Inject active theme CSS vars into WebContainer when it's running or after refresh
  useEffect(() => {
    if (!webContainerUrl || !activeThemeCss) return;
    // Small delay after refresh to ensure iframe has reloaded
    const timer = setTimeout(() => injectThemeIntoWebContainer(activeThemeCss), 300);
    return () => clearTimeout(timer);
  }, [webContainerUrl, activeThemeCss, refreshKey]);

  // Terminal state for WebContainer logs
  const [terminalVisible, setTerminalVisible] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState<string[]>([]);
  const [terminalCmd, setTerminalCmd] = useState('');
  const [terminalRunning, setTerminalRunning] = useState(false);
  const [terminalFilter, setTerminalFilter] = useState<import('./LivePreviewTerminal').TerminalFilterMode>('all');
  const [aiActivityLog, setAiActivityLog] = useState<string[]>([]);
  const terminalLogRef = useRef<(line: string) => void>((line: string) => {
    setTerminalOutput((prev) => {
      const next = [...prev, line];
      // Cap at 500 lines to prevent memory bloat
      return next.length > 500 ? next.slice(-400) : next;
    });
  });

  // Listen for AI activity events from the meeting
  useEffect(() => {
    const handler = (e: Event) => {
      const { agent, file, action } = (e as CustomEvent).detail ?? {};
      if (agent && file) {
        const time = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setAiActivityLog((prev) => {
          const entry = `[${time}] ${agent} ${action || 'updated'}: ${file}`;
          const next = [...prev, entry];
          return next.length > 200 ? next.slice(-150) : next;
        });
      }
    };
    window.addEventListener('tasking.aiActivity', handler);
    return () => window.removeEventListener('tasking.aiActivity', handler);
  }, []);

  const [containerStopped, setContainerStopped] = useState(false);

  const handleStopServer = useCallback(async () => {
    terminalLogRef.current('\n[System] Stopping server...\n');
    await teardownWebContainer();
    setWebContainerUrl(null);
    setContainerStopped(true);
    terminalLogRef.current('[System] Server stopped.\n');
  }, []);

  const handleRestartServer = useCallback(async () => {
    terminalLogRef.current('\n[System] Restarting server...\n');
    await teardownWebContainer();
    setWebContainerUrl(null);
    setContainerStopped(false);
    // Trigger re-mount by bumping a counter — the useEffect that starts the container
    // watches the file list, so we force a refresh
    terminalLogRef.current('[System] Starting server...\n');
    // Small delay to let teardown complete
    setTimeout(() => {
      setContainerStopped(false);
    }, 500);
  }, []);

  const handleTerminalCommand = useCallback(async () => {
    const cmd = terminalCmd.trim();
    if (!cmd || terminalRunning) return;
    setTerminalCmd('');
    terminalLogRef.current(`\n$ ${cmd}\n`);
    setTerminalRunning(true);
    try {
      const parts = cmd.split(/\s+/);
      const command = parts[0];
      const args = parts.slice(1);
      const exitCode = await spawnInWebContainer(command, args, (chunk) => {
        terminalLogRef.current(chunk);
      });
      terminalLogRef.current(`\nProcess exited with code ${exitCode}\n`);
    } catch (err: any) {
      terminalLogRef.current(`\nError: ${err?.message || String(err)}\n`);
    } finally {
      setTerminalRunning(false);
    }
  }, [terminalCmd, terminalRunning]);

  // Safety timeout: auto-clear isBuilding after 90s to prevent stuck spinner
  // WebContainer npm install can take 60+ seconds for large dependency trees
  useEffect(() => {
    if (isBuilding) {
      buildTimeoutRef.current = window.setTimeout(() => {
        console.debug('[build] Safety timeout: clearing isBuilding after 90s');
        setIsBuilding(false);
      }, 90000);
    } else if (buildTimeoutRef.current) {
      window.clearTimeout(buildTimeoutRef.current);
      buildTimeoutRef.current = null;
    }
    return () => {
      if (buildTimeoutRef.current) {
        window.clearTimeout(buildTimeoutRef.current);
        buildTimeoutRef.current = null;
      }
    };
  }, [isBuilding]);

  // Error state removed — all errors handled silently, nothing shown to user

  // Check if this is a non-web project
  const hasPackageJson = sessionFiles.some(
    (f) =>
      f.fileName === 'package.json' && (f.filePath === '/' || f.filePath === '' || !f.filePath),
  );

  // Detect common framework projects (React/Next/Node)
  const isFrameworkProject = useMemo(() => {
    try {
      const pkgFile = sessionFiles.find(
        (f) =>
          f.fileName === 'package.json' && (f.filePath === '/' || f.filePath === '' || !f.filePath),
      );
      if (pkgFile && pkgFile.content) {
        const pkg = JSON.parse(String(pkgFile.content || '{}'));
        const deps = Object.assign(
          {},
          pkg.dependencies || {},
          pkg.devDependencies || {},
          pkg.peerDependencies || {},
        );
        const depKeys = Object.keys(deps);
        return (
          depKeys.includes('react') ||
          depKeys.includes('next') ||
          depKeys.includes('express') ||
          depKeys.includes('@remix-run/react')
        );
      }
    } catch {
      // Ignore parse errors
    }
    return false;
  }, [sessionFiles]);

  // Non-web project file types
  const nonWebFileExtensions = [
    '.gd',
    '.tscn',
    '.escn',
    '.scn',
    '.godot',
    '.tres',
    '.res',
    '.unity',
    '.prefab',
    '.asset',
    '.mat',
    '.anim',
    '.meta',
    '.uproject',
    '.uasset',
    '.umap',
    '.uplugin',
    '.cryproj',
    '.level',
    '.ent',
    '.cgf',
    '.chr',
    '.tq',
    '.tqscene',
    '.tqcfg',
    '.xcodeproj',
    '.xcworkspace',
    '.pbxproj',
    '.gradle',
    '.apk',
    '.aab',
    '.sln',
    '.vcxproj',
    '.csproj',
    '.dll',
    '.exe',
    '.app',
    '.dmg',
    '.blend',
    '.blend1',
    '.max',
    '.3ds',
  ];

  const hasNonWebFiles = sessionFiles.some(
    (f) =>
      nonWebFileExtensions.some((ext) => f.fileName.endsWith(ext)) ||
      f.fileName === 'project.godot' ||
      f.fileName.endsWith('.uproject'),
  );

  // Detect Godot projects
  const isGodotProject = useMemo(() => {
    return sessionFiles.some(
      (f) =>
        f.fileName === 'project.godot' ||
        f.fileName.endsWith('.gd') ||
        f.fileName.endsWith('.tscn') ||
        f.fileName.endsWith('.escn') ||
        f.fileName.endsWith('.tres') ||
        f.fileName.endsWith('.res'),
    );
  }, [sessionFiles]);

  // Detect HTML pages for navigation
  const htmlPages = useMemo(() => {
    const pages: { path: string; displayName: string; fileName: string }[] = [];

    const fileToRoute = (f: Selectable<SessionFiles>) => {
      const isHtml = f.fileName.endsWith('.html');
      if (isHtml) {
        const path =
          f.filePath === '/' || f.filePath === '' || !f.filePath
            ? `/${f.fileName}`
            : `${f.filePath.replace(/\/$/, '')}/${f.fileName}`;
        let displayName = f.fileName.replace('.html', '');
        if (displayName === 'index') displayName = 'Home';
        else displayName = safeCap(displayName);
        return { path, displayName, fileName: f.fileName };
      }

      // SPA pages
      if (/(^|\/)pages(\/|$)/.test(f.filePath) || /(^|\/)src\/pages(\/|$)/.test(f.filePath)) {
        const pathPartMatch = f.filePath.match(/(^|.*)pages\/?(.*)$/);
        let remainder = '';
        if (pathPartMatch) remainder = pathPartMatch[2] || '';
        const base = f.fileName.replace(/\.(tsx|jsx|ts|js)$/i, '');
        if (base === 'index') {
          remainder = remainder.replace(/\/$/, '');
          const path = `/${remainder}`.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
          const displayName = remainder === '' ? 'Home' : remainder.split('/').pop() || 'Page';
          return {
            path,
            displayName: safeCap(displayName),
            fileName: f.fileName,
          };
        }
        const segments = [...(remainder ? remainder.split('/').filter(Boolean) : [])];
        const fileParts = base.split('.');
        fileParts.forEach((p) => {
          if (p.startsWith('$')) segments.push(`:${p.slice(1)}`);
          else if (/^\[(.+)\]$/.test(p)) segments.push(`:${p.replace(/\[|\]/g, '')}`);
          else segments.push(p);
        });
        const path = `/${segments.join('/')}`.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
        const displayName = segments[segments.length - 1] || base || f.fileName;
        return {
          path,
          displayName: safeCap(displayName),
          fileName: f.fileName,
        };
      }
      return null;
    };

    for (const f of sessionFiles) {
      const route = fileToRoute(f as Selectable<SessionFiles>);
      if (route) pages.push(route);
    }

    const uniqMap = new Map<string, { path: string; displayName: string; fileName: string }>();
    pages.forEach((p) => {
      if (!uniqMap.has(p.path)) uniqMap.set(p.path, p);
    });

    return Array.from(uniqMap.values()).sort((a, b) => {
      if (a.fileName === 'index.html' || a.path === '/') return -1;
      if (b.fileName === 'index.html' || b.path === '/') return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [sessionFiles]);

  const currentPage = selectedPage ?? internalSelectedPage ?? htmlPages[0]?.path;
  const imageFiles = useMemo(
    () =>
      sessionFiles.filter((f) => {
        const ext = f.fileName.split('.').pop()?.toLowerCase() || '';
        return IMAGE_EXTS.includes(ext as (typeof IMAGE_EXTS)[number]);
      }),
    [sessionFiles],
  );

  useEffect(() => {
    if (selectedPage !== undefined) {
      setInternalSelectedPage(selectedPage);
    }
  }, [selectedPage]);

  // Auto-show Godot web editor when a Godot project is detected
  useEffect(() => {
    if (isGodotProject && !showGodotEditor) {
      setShowGodotEditor(true);
    }
  }, [isGodotProject, showGodotEditor]);

  // Check if Godot HTML5 export files exist (can run in browser)
  const hasGodotHTML5Export = useMemo(() => {
    const hasHtml = sessionFiles.some((f) => f.fileName === 'index.html');
    const hasPck = sessionFiles.some((f) => f.fileName.endsWith('.pck'));
    const hasWasm = sessionFiles.some((f) => f.fileName.endsWith('.wasm'));
    return hasHtml && (hasPck || hasWasm);
  }, [sessionFiles]);

  const currentHtmlFile = useMemo(() => {
    let htmlFile: Selectable<SessionFiles> | undefined;
    if (currentPage) {
      const pagePath = currentPage.startsWith('/') ? currentPage.slice(1) : currentPage;
      const pathParts = pagePath.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const filePath = pathParts.length > 1 ? '/' + pathParts.slice(0, -1).join('/') : '/';

      htmlFile = sessionFiles.find(
        (f) =>
          f.fileName === fileName &&
          (f.filePath === filePath ||
            f.filePath === filePath.replace(/\/$/, '') ||
            (!f.filePath && filePath === '/')),
      );
    }
    if (!htmlFile) htmlFile = sessionFiles.find((f) => f.fileName === 'index.html');
    if (!htmlFile)
      htmlFile = sessionFiles.find(
        (f) => f.fileName.endsWith('.html') || f.fileName.endsWith('.htm'),
      );
    return htmlFile || null;
  }, [sessionFiles, currentPage]);

  const shouldShowMediaOnlyFallback = useMemo(() => {
    if (imageFiles.length === 0 || !currentHtmlFile?.content) return false;
    const html = String(currentHtmlFile.content || '');
    const hasVisualMarkup = /<(img|svg|canvas|video|audio|main|section|article)\b/i.test(html);
    if (hasVisualMarkup) return false;
    // Detect common placeholder shells: empty body with only root/app mount + scripts
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const body = bodyMatch ? bodyMatch[1] : html;
    const stripped = body
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<link[^>]*>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!stripped) return true;
    const rootOnly =
      /^<div[^>]*(id=["']?(?:root|app|__next)["']?)?[^>]*>\s*<\/div>$/i.test(stripped) ||
      /^<main[^>]*(id=["']?(?:root|app|__next)["']?)?[^>]*>\s*<\/main>$/i.test(stripped);
    return rootOnly;
  }, [imageFiles.length, currentHtmlFile]);

  const mediaOnlyFallbackSrc = useMemo(() => {
    const firstImage = imageFiles[0];
    if (!firstImage) return null;
    const isBase64 =
      firstImage.content?.startsWith('data:image/') ||
      firstImage.content?.startsWith('data:application/');
    const isSvg = firstImage.fileName.toLowerCase().endsWith('.svg');
    if (isBase64) return firstImage.content;
    if (isSvg)
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(firstImage.content || '')}`;
    return null;
  }, [imageFiles]);

  // ── ALTERNATIVE RUNTIME DETECTION ──
  const alternativeRuntime = useOptionalAlternativeRuntime();

  const isPythonProject = useMemo(() => {
    return sessionFiles.some(
      (f) =>
        f.fileName.endsWith('.py') ||
        f.fileName === 'requirements.txt' ||
        f.fileName === 'pyproject.toml' ||
        f.fileName === 'Pipfile',
    );
  }, [sessionFiles]);

  const isFlutterProject = useMemo(() => {
    return sessionFiles.some((f) => f.fileName.endsWith('.dart') || f.fileName === 'pubspec.yaml');
  }, [sessionFiles]);

  const isRustProject = useMemo(() => {
    return sessionFiles.some((f) => f.fileName.endsWith('.rs') || f.fileName === 'Cargo.toml');
  }, [sessionFiles]);

  const isRustWasmSpa = useMemo(() => {
    const hasIndex = sessionFiles.some(
      (f) => f.fileName === 'index.html' && (!f.filePath || f.filePath === '/'),
    );
    const hasIndexJs = sessionFiles.some(
      (f) => f.fileName === 'index.js' && (!f.filePath || f.filePath === '/'),
    );
    const hasWasm = sessionFiles.some((f) => f.fileName.endsWith('.wasm'));
    const hasRustIndicators = sessionFiles.some(
      (f) => f.fileName.endsWith('.rs') || f.fileName === 'Cargo.toml',
    );
    return hasIndex && hasIndexJs && hasWasm && (hasRustIndicators || isRustProject);
  }, [sessionFiles, isRustProject]);

  const isGoProject = useMemo(() => {
    return sessionFiles.some((f) => f.fileName.endsWith('.go') || f.fileName === 'go.mod');
  }, [sessionFiles]);

  const isPhpProject = useMemo(() => {
    return sessionFiles.some(
      (f) =>
        f.fileName.endsWith('.php') || f.fileName === 'composer.json' || f.fileName === 'artisan',
    );
  }, [sessionFiles]);

  const isExpressProject = useMemo(() => {
    // Check package.json for express dependency
    const pkgFile = sessionFiles.find(
      (f) =>
        f.fileName === 'package.json' && (f.filePath === '/' || f.filePath === '' || !f.filePath),
    );
    if (pkgFile?.content) {
      try {
        const pkg = JSON.parse(String(pkgFile.content));
        const deps = { ...pkg.dependencies, ...pkg.devDependencies };
        if ('express' in deps) return true;
      } catch {
        /* ignore */
      }
    }
    // Check for express imports in JS files
    return sessionFiles.some(
      (f) =>
        f.fileName.endsWith('.js') &&
        f.content &&
        (f.content.includes("require('express')") ||
          f.content.includes('require("express")') ||
          f.content.includes("from 'express'") ||
          f.content.includes('from "express"')),
    );
  }, [sessionFiles]);

  // Mobile shell projects - these need local build (Android Studio/Xcode)
  const isMobileShellProject = useMemo(() => {
    return (
      templateId === 'android-shell' || templateId === 'ios-shell' || templateId === 'mobile-shell'
    );
  }, [templateId]);

  // Unity projects
  const isUnityProject = useMemo(() => {
    return (
      templateId?.startsWith('unity') ||
      sessionFiles.some(
        (f) => f.fileName.endsWith('.unity') || f.fileName === 'ProjectSettings/ProjectVersion.txt',
      )
    );
  }, [templateId, sessionFiles]);

  // Unreal projects - download only, no preview
  const isUnrealProject = useMemo(() => {
    return (
      templateId === 'unreal' ||
      sessionFiles.some((f) => f.fileName.endsWith('.uproject') || f.fileName.endsWith('.uasset'))
    );
  }, [templateId, sessionFiles]);

  const detectedAlternativeRuntime: RuntimeType = useMemo(() => {
    if (isPythonProject) return 'pyodide';
    if (isFlutterProject) return 'dartpad';
    if (isRustProject) return 'rust-playground';
    if (isGoProject) return 'go-playground';
    if (isPhpProject) return 'php-playground';
    if (isGodotProject) return 'godot-editor';
    if (isExpressProject) return 'express-playground';
    return null;
  }, [
    isPythonProject,
    isFlutterProject,
    isRustProject,
    isGoProject,
    isPhpProject,
    isGodotProject,
    isExpressProject,
  ]);

  const isAlternativeRuntimeProject = detectedAlternativeRuntime !== null;

  // Auto-generate alternative runtime preview
  useEffect(() => {
    if (!alternativeRuntime || !isAlternativeRuntimeProject || sessionFiles.length === 0) return;

    const filesMap: Record<string, string> = {};
    sessionFiles.forEach((file) => {
      const rawPath =
        file.filePath === '/' || file.filePath === '' || !file.filePath
          ? file.fileName
          : `${file.filePath.replace(/^\//, '').replace(/\/$/, '')}/${file.fileName}`;
      filesMap[normalizePath(rawPath)] = file.content || '';
    });

    const generatePreview = async () => {
      try {
        if (isPythonProject) await alternativeRuntime!.generatePythonPreview(filesMap);
        else if (isFlutterProject) await alternativeRuntime!.generateFlutterPreview(filesMap);
        else if (isRustProject) await alternativeRuntime!.generateRustPreview(filesMap);
        else if (isGoProject) await alternativeRuntime!.generateGoPreview(filesMap);
        else if (isPhpProject) await alternativeRuntime!.generatePhpPreview(filesMap);
        else if (isGodotProject) await alternativeRuntime!.generateGodotPreview(filesMap);
        else if (isExpressProject) await alternativeRuntime!.generateExpressPreview(filesMap);
      } catch (err) {
        console.error('[LivePreview] Failed to generate alternative runtime preview:', err);
      }
    };
    generatePreview();
  }, [
    alternativeRuntime,
    isAlternativeRuntimeProject,
    isPythonProject,
    isFlutterProject,
    isRustProject,
    isGoProject,
    isPhpProject,
    isGodotProject,
    isExpressProject,
    sessionFiles,
  ]);

  // Only consider it non-web if: has files, no package.json, AND has non-web file types
  const isNonWebProject =
    sessionFiles.length > 0 &&
    !hasPackageJson &&
    hasNonWebFiles &&
    !hasGodotHTML5Export &&
    !isAlternativeRuntimeProject;

  // ── Rust auto-publish ──
  const publishTimerRef = useRef<number | null>(null);
  const lastFilesSnapshotRef = useRef<string | null>(null);
  const [isPublishingRust, setIsPublishingRust] = useState(false);

  const publishRust = useCallback(async () => {
    setIsPublishingRust(true);
    try {
      await publishRustPreview(sessionId);
      window.dispatchEvent(new CustomEvent('vibes.published', { detail: { sessionId } }));
      toast.success('Published Rust preview');
    } catch (err) {
      console.error('Failed to publish Rust preview:', err);
      toast.error('Failed to publish Rust preview');
    } finally {
      setIsPublishingRust(false);
    }
  }, [sessionId]);

  const [autoPublishEnabled, setAutoPublishEnabled] = useState<boolean>(() => {
    try {
      const val = localStorage.getItem(`rust_auto_publish_${sessionId}`);
      return val === null ? true : val === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(`rust_auto_publish_${sessionId}`, String(autoPublishEnabled));
    } catch {
      /* ignore */
    }
  }, [sessionId, autoPublishEnabled]);

  useEffect(() => {
    if (!autoPublishEnabled || !isRustProject) return;

    const snapshot = JSON.stringify(
      sessionFiles.map((f) => ({ path: f.filePath, name: f.fileName, content: f.content })),
    );
    if (snapshot === lastFilesSnapshotRef.current) return;
    lastFilesSnapshotRef.current = snapshot;

    if (publishTimerRef.current) window.clearTimeout(publishTimerRef.current);
    publishTimerRef.current = window.setTimeout(() => {
      publishRust().catch((err) => console.error('Auto-publish failed:', err));
      publishTimerRef.current = null;
    }, 2500);

    return () => {
      if (publishTimerRef.current) {
        window.clearTimeout(publishTimerRef.current);
        publishTimerRef.current = null;
      }
    };
  }, [sessionFiles, isRustProject, publishRust, autoPublishEnabled]);

  // ── Detect framework from package.json deps ──
  const detectedWebFramework = useMemo((): 'react' | 'vue' | 'svelte' | 'angular' | 'vanilla' => {
    try {
      const pkgFile = sessionFiles.find(
        (f) =>
          f.fileName === 'package.json' && (f.filePath === '/' || f.filePath === '' || !f.filePath),
      );
      if (pkgFile?.content) {
        const pkg = JSON.parse(String(pkgFile.content));
        const deps = { ...pkg.dependencies, ...pkg.devDependencies };
        if ('@angular/core' in deps) return 'angular';
        // Next.js uses React pipeline
        if ('react' in deps || 'react-dom' in deps || 'next' in deps) return 'react';
        if ('vue' in deps) return 'vue';
        if ('svelte' in deps) return 'svelte';
      }
    } catch {
      /* ignore */
    }
    // Check file extensions as fallback
    if (sessionFiles.some((f) => f.fileName.endsWith('.tsx') || f.fileName.endsWith('.jsx')))
      return 'react';
    if (sessionFiles.some((f) => f.fileName.endsWith('.vue'))) return 'vue';
    if (sessionFiles.some((f) => f.fileName.endsWith('.svelte'))) return 'svelte';
    return 'vanilla';
  }, [sessionFiles]);

  // ── Check if project needs esbuild (has JSX/TSX/Vue/Svelte files, or Angular) ──
  // Detect vanilla JS projects with Vite config (Three.js, PlayCanvas, etc.)
  // that need WebContainer but don't have .tsx/.jsx/.vue/.svelte files
  const hasVanillaViteProject = useMemo(
    () =>
      detectedWebFramework === 'vanilla' &&
      sessionFiles.some(
        (f) =>
          (f.fileName === 'vite.config.ts' || f.fileName === 'vite.config.js') &&
          (f.filePath === '/' || f.filePath === '' || !f.filePath),
      ),
    [sessionFiles, detectedWebFramework],
  );

  const requiresBundling = useMemo(
    // Default to "bundleable" while files are still loading to avoid initial /preview 404 fetches.
    () =>
      sessionFiles.length === 0 ||
      needsEsbuildBundling(sessionFiles) ||
      hasVanillaViteProject ||
      detectedWebFramework === 'react' ||
      detectedWebFramework === 'vue' ||
      detectedWebFramework === 'svelte' ||
      detectedWebFramework === 'angular',
    [sessionFiles, detectedWebFramework, hasVanillaViteProject],
  );

  // Prefer Worker Pages URL previews for web templates when available.
  // Keep in-browser srcdoc/esbuild as fallback when no published URL exists yet.
  const shouldUseWorkerPagesPreview = useMemo(
    () =>
      !isAlternativeRuntimeProject &&
      !isNonWebProject &&
      !isMobileShellProject &&
      !isUnrealProject,
    [isAlternativeRuntimeProject, isNonWebProject, isMobileShellProject, isUnrealProject],
  );

  // Vibes preview hook — publish action + preview URL.
  // For bundleable (srcdoc) sessions, skip eager fetch to avoid noisy /preview 404s
  // until a preview has actually been published.
  const { previewUrl: vibesPreviewUrl, publish: vibesPublish } = useVibePreview(sessionId, {
    skipFetch: !shouldUseWorkerPagesPreview || requiresBundling,
  });

  // Preview URL: published site or alternative runtime
  const effectivePreviewUrl = vibesPreviewUrl || alternativeRuntime?.previewUrl || null;

  // Replace framework boilerplate starter screens with a branded splash in live preview.
  const shouldShowStarterSplash = useMemo(() => {
    if (!ENABLE_STARTER_SPLASH) return false;
    if (isNonWebProject || sessionFiles.length === 0) return false;
    const starterEntryNames = new Set([
      'app.tsx',
      'app.jsx',
      'app.vue',
      'app.svelte',
      'app.component.ts',
      'page.tsx',
      'index.html',
      'main.js',
    ]);

    // As soon as starter entry files are edited (typically by chat/codegen),
    // stop showing the splash and render the actual generated app.
    const starterEntries = sessionFiles.filter((f) =>
      starterEntryNames.has(f.fileName.toLowerCase()),
    );
    const hasEditedStarterEntry = starterEntries.some((f) => {
      if (!f.createdAt || !f.updatedAt) return false;
      const created = new Date(f.createdAt as any).getTime();
      const updated = new Date(f.updatedAt as any).getTime();
      return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2000;
    });
    if (hasEditedStarterEntry) return false;

    // Also check if ANY file in the session has been edited (not just entry files).
    // The AI might create new components or modify non-entry files first.
    const hasAnyEditedFile = sessionFiles.some((f) => {
      if (!f.createdAt || !f.updatedAt) return false;
      const created = new Date(f.createdAt as any).getTime();
      const updated = new Date(f.updatedAt as any).getTime();
      return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2000;
    });
    if (hasAnyEditedFile) return false;

    const starterSignatures: Array<{ fileName: string; markers: string[]; maxLength: number }> = [
      {
        fileName: 'App.tsx',
        markers: ['hello, vite + react!'],
        maxLength: 2200,
      },
      {
        fileName: 'App.vue',
        markers: ['hello, vite + vue!'],
        maxLength: 2600,
      },
      {
        fileName: 'App.svelte',
        markers: ['hello, svelte!'],
        maxLength: 2200,
      },
      {
        fileName: 'app.component.ts',
        markers: ['hello angular!'],
        maxLength: 2600,
      },
      {
        fileName: 'App.tsx',
        markers: ['shadcn + tailwind starter', 'example button'],
        maxLength: 2600,
      },
      {
        fileName: 'page.tsx',
        markers: ["welcome to <span style={{ color: '#0070f3' }}>next.js</span>"],
        maxLength: 3200,
      },
      {
        fileName: 'index.html',
        markers: ['hello, vite!'],
        maxLength: 2800,
      },
      {
        fileName: 'main.js',
        markers: ['hello, vite!'],
        maxLength: 2200,
      },
    ];

    return starterSignatures.some((signature) => {
      const file = sessionFiles.find((f) => f.fileName === signature.fileName);
      if (!file?.content) return false;
      const content = String(file.content || '');
      const lowered = content.toLowerCase();
      const hasMarkers = signature.markers.every((marker) => lowered.includes(marker));
      if (!hasMarkers) return false;
      return content.length <= signature.maxLength;
    });
  }, [sessionFiles, isNonWebProject]);

  const starterSplashSrcDoc = useMemo(() => {
    if (!shouldShowStarterSplash || starterSplashExpired) return null;
    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Tasking Preview</title>
    <style>
      :root { color-scheme: dark light; }
      html, body {
        margin: 0;
        width: 100%;
        height: 100%;
        font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        background: radial-gradient(circle at 20% 20%, #231b2f 0%, #13111a 45%, #0b0a10 100%);
        color: #f6f2ff;
      }
      .wrap {
        min-height: 100%;
        display: grid;
        place-items: center;
        padding: 24px;
      }
      .card {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        width: min(520px, calc(100vw - 40px));
        padding: clamp(16px, 3vw, 24px);
        border-radius: 16px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
      }
      .logo {
        width: min(340px, 78vw);
        max-width: 100%;
        height: auto;
        display: block;
        object-fit: contain;
      }
      .hint {
        opacity: 0.85;
        font-size: 0.9rem;
        text-align: center;
      }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="card">
        <img class="logo" src="/logo-start.png" alt="Tasking start" />
        <div class="hint">Start building in chat to replace this starter view.</div>
      </div>
    </div>
    <script>
      (function () {
        var ready = function () {
          try { window.parent.postMessage({ type: 'preview-ready' }, '*'); } catch (e) {}
        };
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          ready();
        } else {
          document.addEventListener('DOMContentLoaded', ready, { once: true });
        }
        window.addEventListener('load', ready, { once: true });
      })();
    </script>
    ${PREVIEW_NAV_GUARD_SCRIPT}
  </body>
</html>`;
  }, [shouldShowStarterSplash, starterSplashExpired]);

  // Never allow the starter splash to persist indefinitely; show actual preview output.
  useEffect(() => {
    if (!shouldShowStarterSplash) {
      if (starterSplashExpired) setStarterSplashExpired(false);
      return;
    }

    const t = window.setTimeout(() => {
      setStarterSplashExpired(true);
    }, 4000);

    return () => window.clearTimeout(t);
  }, [shouldShowStarterSplash, refreshKey, starterSplashExpired]);

  // ── Naive srcDoc for pure HTML/CSS/JS projects (no build step needed) ──
  const naiveSrcDoc = useMemo(() => {
    if (isNonWebProject || requiresBundling) return null;

    // Find the HTML file to display
    let htmlFile: Selectable<SessionFiles> | undefined;

    if (selectedPage) {
      const pagePath = selectedPage.startsWith('/') ? selectedPage.slice(1) : selectedPage;
      const pathParts = pagePath.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const filePath = pathParts.length > 1 ? '/' + pathParts.slice(0, -1).join('/') : '/';

      htmlFile = sessionFiles.find(
        (f) =>
          f.fileName === fileName &&
          (f.filePath === filePath ||
            f.filePath === filePath.replace(/\/$/, '') ||
            (!f.filePath && filePath === '/')),
      );
    }

    if (!htmlFile) htmlFile = sessionFiles.find((f) => f.fileName === 'index.html');
    if (!htmlFile)
      htmlFile = sessionFiles.find(
        (f) => f.fileName.endsWith('.html') || f.fileName.endsWith('.htm'),
      );
    if (!htmlFile) return null;

    let content = String(htmlFile.content || '');

    // Inline CSS files
    const cssFiles = sessionFiles.filter((f) => f.fileName.endsWith('.css'));

    // Detect Tailwind early so we can use the right <style> type when inlining CSS.
    // @theme, @apply, @layer base/utilities require type="text/tailwindcss" to be
    // processed by @tailwindcss/browser@4 — plain <style> silently ignores them.
    const hasTailwindCss = cssFiles.some(
      (css) =>
        css.content?.includes('@import "tailwindcss"') ||
        css.content?.includes("@import 'tailwindcss'") ||
        css.content?.includes('@tailwind base') ||
        css.content?.includes('@theme') ||
        css.content?.includes('@apply') ||
        css.content?.includes('@layer'),
    );
    const hasTailwindHtmlClasses =
      /class="[^"]*(?:flex |grid |p-\d|m-\d|text-|bg-|rounded|w-\d|h-\d|gap-|justify-|items-)/.test(
        content,
      );
    const isTailwindProject = hasTailwindCss || hasTailwindHtmlClasses;

    // Helper: convert session file to data URL for CSS url() references
    const getFileDataUrl = (relPath: string): string | null => {
      const normalized = relPath
        .replace(/^['"]|['"]$/g, '')
        .replace(/^\.\//, '/')
        .replace(/^([^/])/, '/$1');
      const found = sessionFiles.find((f) => {
        const fp = (
          (f.filePath === '/' || !f.filePath ? '' : f.filePath) +
          '/' +
          f.fileName
        ).replace(/\/+/g, '/');
        return fp === normalized || '/' + f.fileName === normalized;
      });
      if (!found || !found.content) return null;
      const ext = found.fileName.split('.').pop()?.toLowerCase() || '';
      const mimeMap: Record<string, string> = {
        woff2: 'font/woff2',
        woff: 'font/woff',
        ttf: 'font/ttf',
        otf: 'font/otf',
        eot: 'application/vnd.ms-fontobject',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        svg: 'image/svg+xml',
        webp: 'image/webp',
        ico: 'image/x-icon',
        glb: 'model/gltf-binary',
        gltf: 'model/gltf+json',
        obj: 'text/plain',
        fbx: 'application/octet-stream',
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        mp4: 'video/mp4',
        webm: 'video/webm',
        wasm: 'application/wasm',
      };
      const mime = mimeMap[ext] || 'application/octet-stream';
      // If content is already a data URL, return it directly
      if (found.content.startsWith('data:')) return found.content;
      // For text-based formats (SVG), use text encoding; for binary use base64
      const isText = ['svg'].includes(ext);
      if (isText) return `data:${mime};charset=utf-8,${encodeURIComponent(found.content)}`;
      // For binary files stored as content string, convert via btoa
      try {
        return `data:${mime};base64,${btoa(found.content)}`;
      } catch {
        return null;
      }
    };

    cssFiles.forEach((css) => {
      const dir =
        css.filePath === '/' || css.filePath === '' || !css.filePath
          ? ''
          : css.filePath.replace(/\/$/, '');
      const fullPath = (dir + '/' + css.fileName).replace(/\/+/g, '/');
      const fileName = css.fileName;
      // Escape </style patterns in CSS content to prevent premature style tag closing
      const rawCss = (css.content || '')
        .replace(/@import\s+["']tailwindcss["'];?/g, '') // strip — browser script handles it
        .replace(/@import\s+["']tailwindcss\/[\w-]+["'];?/g, '')
        .replace(/@import\s+url\(\s*["']?tailwindcss(?:\/[\w-]+)?["']?\s*\)\s*;?/g, '')
        // Strip Tailwind v3 directives — @tailwindcss/browser@4 doesn't understand them
        // and they cause silent failures resulting in blank previews
        .replace(/@tailwind\s+base\s*;?/g, '')
        .replace(/@tailwind\s+components\s*;?/g, '')
        .replace(/@tailwind\s+utilities\s*;?/g, '')
        .replace(/@tailwind\s+variants\s*;?/g, '')
        .replace(/@import\s+["']tw-animate-css["'];?/g, '') // strip unsupported bare import
        .replace(/@import\s+url\(\s*["']?tw-animate-css["']?\s*\)\s*;?/g, '')
        // Strip @source directives — they reference disk paths that can't be resolved
        // in srcdoc iframes and abort the entire Tailwind browser compilation
        .replace(/@source\s+["'][^"']*["']\s*;?/g, '')
        .replace(/@source\s+[^\s;]+\s*;?/g, '')
        // Strip @plugin directives — npm plugins may not be Tailwind v4-compatible
        // and a single failed plugin aborts ALL utility generation
        .replace(/@plugin\s+["'][^"']*["']\s*;?/g, '')
        .replace(/<\/style/gi, '<\\/style');
      // Replace relative url() references in CSS with data URLs from session files
      const processedRawCss = rawCss.replace(
        /url\(\s*(['"]?)(?!https?:\/\/|data:)([^'")]+)\1\s*\)/g,
        (_m: string, _q: string, path: string) => {
          const dataUrl = getFileDataUrl(path);
          return dataUrl ? `url("${dataUrl}")` : _m;
        },
      );
      // When Tailwind is active, use type="text/tailwindcss" so @theme/@apply/@layer
      // are processed by @tailwindcss/browser@4 (plain <style> silently ignores them)
      const styleType = isTailwindProject ? ' type="text/tailwindcss"' : '';
      const inlineTag = `<style${styleType}>\n${processedRawCss}\n</style>`;
      // Match link tags with: relative path (./styles.css), bare name (styles.css),
      // and absolute path (/src/styles/globals.css) - all variations the AI may generate
      const escapedFull = fullPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // regex1: exact full path with optional leading ./ or /
      content = content.replace(
        new RegExp(`<link[^>]*href=["'](?:\\./)?${escapedFull}["'][^>]*>`, 'gi'),
        inlineTag,
      );
      // regex2: bare filename with optional leading ./
      content = content.replace(
        new RegExp(`<link[^>]*href=["'](?:\\./)?${escapedName}["'][^>]*>`, 'gi'),
        inlineTag,
      );
      // regex3: absolute path like /src/styles/globals.css
      if (fullPath.startsWith('/')) {
        content = content.replace(
          new RegExp(`<link[^>]*href=["']${escapedFull}["'][^>]*>`, 'gi'),
          inlineTag,
        );
      }
    });

    // Inject CSS files that were NOT referenced via <link> tags in the HTML.
    // AI often generates separate CSS files without adding the <link> tag.
    cssFiles.forEach((css) => {
      if (!css.content) return;
      const dir =
        css.filePath === '/' || css.filePath === '' || !css.filePath
          ? ''
          : css.filePath.replace(/\/$/, '');
      const fullPath = (dir + '/' + css.fileName).replace(/\/+/g, '/');
      const fileName = css.fileName;
      // Check if this CSS file was already inlined (matched a <link> tag)
      const escapedName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedFull = fullPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const isReferenced =
        new RegExp(`<link[^>]*href=["'][^"']*${escapedName}["']`, 'i').test(htmlFile!.content) ||
        new RegExp(`<link[^>]*href=["'][^"']*${escapedFull}["']`, 'i').test(htmlFile!.content);
      if (isReferenced) return; // Already inlined by the replacement above
      // Not referenced — inject as inline <style> into <head>
      const rawCss = (css.content || '')
        .replace(/@source\s+["'][^"']*["']\s*;?/g, '')
        .replace(/@source\s+[^\s;]+\s*;?/g, '')
        .replace(/@plugin\s+["'][^"']*["']\s*;?/g, '')
        .replace(/<\/style/gi, '<\\/style');
      const styleType = isTailwindProject ? ' type="text/tailwindcss"' : '';
      const inlineTag = `<style${styleType}>\n/* ${fileName} */\n${rawCss}\n</style>`;
      if (content.includes('</head>')) {
        content = content.replace('</head>', `${inlineTag}\n</head>`);
      } else if (content.includes('<body')) {
        content = content.replace(/<body[^>]*>/, `$&\n${inlineTag}`);
      } else {
        content = `${inlineTag}\n${content}`;
      }
    });

    // Inline JS files (plain .js only — no JSX/TSX since we know !requiresBundling)
    const jsFiles = sessionFiles.filter((f) => f.fileName.endsWith('.js'));
    jsFiles.forEach((js) => {
      const fullPath =
        js.filePath === '/' || js.filePath === '' || !js.filePath
          ? js.fileName
          : `${js.filePath.replace(/\/$/, '')}/${js.fileName}`.replace(/\/+/g, '/');
      const fileName = js.fileName;
      const regex1 = new RegExp(
        `<script[^>]*src=["']\\.?/?${fullPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*>.*?</script>`,
        'g',
      );
      const regex2 = new RegExp(
        `<script[^>]*src=["']\\.?/?${fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*>.*?</script>`,
        'g',
      );
      // Escape </script patterns in JS content to prevent premature script tag closing
      const safeJsContent = (js.content || '').replace(/<\/script/gi, '<\\/script');
      content = content.replace(regex1, `<script>\n${safeJsContent}\n</script>`);
      content = content.replace(regex2, `<script>\n${safeJsContent}\n</script>`);
    });

    // Inject JS files that were NOT referenced via <script src> tags in the HTML.
    // AI often generates separate JS files without adding the <script> tag.
    jsFiles.forEach((js) => {
      if (!js.content) return;
      const fullPath =
        js.filePath === '/' || js.filePath === '' || !js.filePath
          ? js.fileName
          : `${js.filePath.replace(/\/$/, '')}/${js.fileName}`.replace(/\/+/g, '/');
      const fileName = js.fileName;
      const escapedName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedFull = fullPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const isReferenced =
        new RegExp(`<script[^>]*src=["'][^"']*${escapedName}["']`, 'i').test(htmlFile!.content) ||
        new RegExp(`<script[^>]*src=["'][^"']*${escapedFull}["']`, 'i').test(htmlFile!.content);
      if (isReferenced) return; // Already inlined by the replacement above
      // Not referenced — inject as inline <script> before </body>
      const safeJsContent = (js.content || '').replace(/<\/script/gi, '<\\/script');
      const inlineTag = `<script>\n/* ${fileName} */\n${safeJsContent}\n</script>`;
      if (content.includes('</body>')) {
        content = content.replace('</body>', `${inlineTag}\n</body>`);
      } else {
        content += `\n${inlineTag}`;
      }
    });

    // Inline relative image src attributes from session files (srcdoc has no origin, so
    // relative paths fail). Replace <img src="./hero.png"> with data URL if found in files.
    const inlineImageFiles = sessionFiles.filter((f) =>
      /\.(png|jpg|jpeg|gif|webp|svg|ico)$/i.test(f.fileName),
    );
    if (inlineImageFiles.length > 0) {
      content = content.replace(
        /(<img[^>]*\bsrc\s*=\s*["'])(?!https?:\/\/|data:)([^"']+)(["'])/gi,
        (_match, pre, src, quote) => {
          const normalized = src.replace(/^\.\//, '/').replace(/^([^/])/, '/$1');
          const imgFile = inlineImageFiles.find((f) => {
            const fullPath = (
              (f.filePath === '/' || !f.filePath ? '' : f.filePath) +
              '/' +
              f.fileName
            ).replace(/\/+/g, '/');
            return fullPath === normalized || '/' + f.fileName === normalized;
          });
          if (imgFile?.content?.startsWith('data:')) {
            return `${pre}${imgFile.content}${quote}`;
          }
          return _match;
        },
      );
    }

    // Inject fetch interceptor for binary session files (GLB, textures, audio, etc.)
    // so that JS code like GLTFLoader.load('/models/model.glb') can resolve session files in srcdoc
    const binaryFiles = sessionFiles.filter((f) =>
      f.content?.startsWith('data:') &&
      /\.(glb|gltf|obj|fbx|png|jpg|jpeg|gif|webp|ico|mp3|wav|ogg|mp4|webm|woff2?|ttf|otf|wasm|pck|hdr|exr)$/i.test(f.fileName),
    );
    if (binaryFiles.length > 0) {
      const fileMap: Record<string, string> = {};
      for (const f of binaryFiles) {
        const fp = (
          (f.filePath === '/' || !f.filePath ? '' : f.filePath) +
          '/' +
          f.fileName
        ).replace(/\/+/g, '/');
        if (f.content) {
          fileMap[fp] = f.content;
          // Also map without /public/ prefix (Vite serves public/ at root)
          if (fp.startsWith('/public/')) {
            fileMap[fp.replace('/public/', '/')] = f.content;
          }
        }
      }
      const fetchInterceptScript = `<script>
(function(){
  var _assetMap = ${JSON.stringify(fileMap)};
  var _origFetch = window.fetch;
  window.fetch = function(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
    var normalized = url.replace(/^\\.\\//, '/').replace(/^\\/\\/+/, '/');
    if (!normalized.startsWith('/')) normalized = '/' + normalized;
    var dataUrl = _assetMap[normalized];
    if (dataUrl) {
      var parts = dataUrl.split(',');
      var meta = parts[0];
      var b64 = parts.slice(1).join(',');
      var mime = (meta.match(/data:([^;]+)/) || [])[1] || 'application/octet-stream';
      var raw = atob(b64);
      var bytes = new Uint8Array(raw.length);
      for (var i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      return Promise.resolve(new Response(bytes.buffer, {
        status: 200,
        headers: { 'Content-Type': mime }
      }));
    }
    return _origFetch.apply(this, arguments);
  };
  // Also intercept XMLHttpRequest for loaders that use XHR (Three.js FileLoader)
  var _origXHROpen = XMLHttpRequest.prototype.open;
  var _origXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url) {
    this._interceptUrl = typeof url === 'string' ? url : '';
    return _origXHROpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function() {
    var url = this._interceptUrl || '';
    var normalized = url.replace(/^\\.\\//, '/').replace(/^\\/\\/+/, '/');
    if (!normalized.startsWith('/')) normalized = '/' + normalized;
    var dataUrl = _assetMap[normalized];
    if (dataUrl) {
      var self = this;
      var parts = dataUrl.split(',');
      var b64 = parts.slice(1).join(',');
      var raw = atob(b64);
      var bytes = new Uint8Array(raw.length);
      for (var i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      Object.defineProperty(self, 'status', { get: function(){ return 200; } });
      Object.defineProperty(self, 'readyState', { get: function(){ return 4; } });
      Object.defineProperty(self, 'response', { get: function(){ return bytes.buffer; } });
      Object.defineProperty(self, 'responseType', { get: function(){ return 'arraybuffer'; }, set: function(){} });
      setTimeout(function(){
        if (self.onload) self.onload(new Event('load'));
        self.dispatchEvent(new Event('load'));
      }, 0);
      return;
    }
    return _origXHRSend.apply(this, arguments);
  };
})();
</script>`;
      if (content.includes('<head>')) {
        content = content.replace('<head>', `<head>\n${fetchInterceptScript}`);
      } else {
        content = fetchInterceptScript + '\n' + content;
      }
    }

    // Inject Tailwind v4 browser script or font baseline
    if (isTailwindProject) {
      const tailwindScript = `<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4" onerror="console.warn('[preview] @tailwindcss/browser@4 CDN failed to load — using fallback CSS')"></script>\n${TAILWIND_DESIGN_BASELINE}`;
      if (content.includes('<head>')) {
        content = content.replace('<head>', `<head>\n${tailwindScript}`);
      } else if (content.includes('<html')) {
        content = content.replace(/<html[^>]*>/, `$&\n<head>${tailwindScript}</head>`);
      } else {
        content = `${tailwindScript}\n${content}`;
      }
      // Strip any remaining @import "tailwindcss" the browser script handles it
      content = content.replace(/@import\s+["']tailwindcss["'];?/g, '');
      content = content.replace(/@import\s+["']tailwindcss\/[\w-]+["'];?/g, '');
      content = content.replace(
        /@import\s+url\(\s*["']?tailwindcss(?:\/[\w-]+)?["']?\s*\)\s*;?/g,
        '',
      );
      content = content.replace(/@import\s+["']tw-animate-css["'];?/g, '');
      content = content.replace(/@import\s+url\(\s*["']?tw-animate-css["']?\s*\)\s*;?/g, '');
      // Strip @source (disk-only, breaks browser compilation) and @plugin (unreliable CDN)
      content = content.replace(/@source\s+["'][^"']*["']\s*;?/g, '');
      content = content.replace(/@source\s+[^\s;]+\s*;?/g, '');
      content = content.replace(/@plugin\s+["'][^"']*["']\s*;?/g, '');
    } else {
      // Always inject font + CSS baseline even for non-Tailwind projects
      const fontScript = FONT_BASELINE;
      if (content.includes('<head>')) {
        content = content.replace('<head>', `<head>\n${fontScript}`);
      } else if (content.includes('<html')) {
        content = content.replace(/<html[^>]*>/, `$&\n<head>${fontScript}</head>`);
      } else {
        content = `${fontScript}\n${content}`;
      }
    }

    // Strip <base> and <meta refresh> tags to prevent inception
    content = content.replace(/<base[^>]*>/gi, '');
    content = content.replace(/<meta[^>]*http-equiv\s*=\s*["']refresh["'][^>]*>/gi, '');
    // Strip analytics/tracker scripts and known noisy third-party styles in srcdoc previews.
    // They add console noise and can break under stricter browser MIME/CSP behavior.
    content = content.replace(
      /<script[^>]*\bsrc\s*=\s*["']https?:\/\/(?:www\.)?googletagmanager\.com\/gtag\/js[^"']*["'][^>]*>\s*<\/script>/gi,
      '',
    );
    content = content.replace(
      /<link[^>]*rel\s*=\s*["']stylesheet["'][^>]*href\s*=\s*["']https?:\/\/cdn\.jsdelivr\.net\/npm\/tw-animate-css[^"']*["'][^>]*>/gi,
      '',
    );

    // Strip any remaining <script src="..."> with relative paths (not yet inlined).
    // These would resolve to tasking.tech/... causing inception.
    content = content.replace(
      /<script[^>]*\bsrc\s*=\s*["'](?!https?:\/\/)[^"']*["'][^>]*>\s*<\/script>/gi,
      '',
    );
    content = content.replace(
      /<script[^>]*\bsrc\s*=\s*["'](?!https?:\/\/)[^"']*["'][^>]*\/>/gi,
      '',
    );

    // Inject navigation guard to prevent anchor/form/programmatic navigation
    // from loading the parent app inside the iframe.
    if (content.includes('</body>')) {
      content = content.replace(
        '</body>',
        `${PREVIEW_NAV_GUARD_SCRIPT}\n${PREVIEW_INSPECTOR_SCRIPT}\n</body>`,
      );
    } else {
      content += `\n${PREVIEW_NAV_GUARD_SCRIPT}\n${PREVIEW_INSPECTOR_SCRIPT}`;
    }

    return content;
  }, [sessionFiles, selectedPage, isNonWebProject, requiresBundling]);

  // ── WebContainer + Vite preview for JSX/TSX/Vue/Svelte projects ──
  useEffect(() => {
    if (!requiresBundling || isNonWebProject || sessionFiles.length === 0) {
      setBuiltSrcDoc(null);
      setWebContainerUrl(null);
      setBuildErrors([]);
      setIsBuilding(false);
      return;
    }

    // Debounce 500ms for WebContainer (slightly longer than esbuild since Vite HMR handles fast updates)
    if (webContainerTimerRef.current) window.clearTimeout(webContainerTimerRef.current);

    webContainerTimerRef.current = window.setTimeout(async () => {
      if (sessionFiles.length === 0 || !requiresBundling || isNonWebProject) return;

      setIsBuilding(true);
      setBuildErrors([]);
      setRuntimeErrors([]);

      try {
        const filesToBundle = sessionFiles.map((f) => ({
          fileName: f.fileName,
          filePath: f.filePath ?? null,
          content: f.content ?? null,
        }));

        console.debug(
          '[webcontainer] Starting preview with',
          filesToBundle.length,
          'files, framework:',
          detectedWebFramework,
        );

        // Listen for Vite transform errors that happen after server-ready
        onWebContainerError((errorText) => {
          console.debug('[webcontainer] Vite error:', errorText);
          setBuildErrors((prev) => {
            // Avoid duplicates
            if (prev.some((e) => e === errorText)) return prev;
            return [...prev, errorText];
          });
        });

        const result = await startWebContainerPreview(
          {
            files: filesToBundle,
            framework: detectedWebFramework,
          },
          sessionId,
          (line) => { console.debug(line); terminalLogRef.current(line); },
        );

        if (result.errors.length > 0) {
          console.debug('[webcontainer] Errors:', result.errors);
          setBuildErrors(result.errors);
        }

        if (result.url) {
          setWebContainerUrl(result.url);
          setBuiltSrcDoc(null); // Clear any old srcDoc
          setBuildErrors([]); // Clear startup errors when URL is ready
          console.debug('[webcontainer] Preview ready at', result.url);
        }
      } catch (err: any) {
        console.debug('[webcontainer] Preview error:', err);
        setBuildErrors([err?.message || String(err)]);
      } finally {
        setIsBuilding(false);
      }
    }, 500) as unknown as number;

    return () => {
      if (webContainerTimerRef.current) {
        window.clearTimeout(webContainerTimerRef.current);
        webContainerTimerRef.current = null;
      }
      onWebContainerError(null); // Unregister error callback
    };
  }, [sessionFiles, requiresBundling, isNonWebProject, detectedWebFramework]);

  // ── Effective srcDoc: esbuild output or naive fallback ──
  const rawSrcDoc =
    normalizePreviewHtml(requiresBundling ? builtSrcDoc : naiveSrcDoc) || starterSplashSrcDoc;

  // Some generated sessions can end up with multiple import maps in srcdoc, which causes
  // browser warnings and dropped rules. Merge all maps into one canonical import map.
  const srcDoc = useMemo(() => {
    if (!rawSrcDoc) return rawSrcDoc;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawSrcDoc, 'text/html');
      const proxyBase =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}/_api/imgproxy?url=`
          : '/_api/imgproxy?url=';
      const toProxyUrl = (url: string) => `${proxyBase}${encodeURIComponent(url)}`;
      const rewriteCssAssetUrls = (cssText: string) =>
        cssText.replace(
          /url\(\s*(['"]?)(https?:\/\/[^'")]+|\/_api\/imgproxy\?url=[^'")]+)\1\s*\)/gi,
          (_m, q, url) => {
            if (/^\/_api\/imgproxy\?url=/i.test(url)) {
              const upstream = url.replace(/^\/_api\/imgproxy\?url=/i, '');
              try {
                return `url(${q}${toProxyUrl(decodeURIComponent(upstream))}${q})`;
              } catch {
                return `url(${q}${proxyBase}${upstream}${q})`;
              }
            }
            return `url(${q}${toProxyUrl(url)}${q})`;
          },
        );
      const themeBridgeMarkup = buildPreviewThemeBridge(effectiveTheme, previewCustomThemeVars);
      doc
        .querySelectorAll('script[data-tasking-preview-theme], style[data-tasking-preview-theme-vars], style[data-tasking-session-theme]')
        .forEach((node) => node.remove());

      // Inject session theme AFTER the baseline so it overrides default design tokens
      const fullThemeBridge = themeBridgeMarkup + (activeSessionThemeStyle || '');
      if (doc.head) {
        doc.head.insertAdjacentHTML('afterbegin', fullThemeBridge);
      } else if (doc.documentElement) {
        doc.documentElement.insertAdjacentHTML('afterbegin', `<head>${fullThemeBridge}</head>`);
      }

      const mapScripts = Array.from(
        doc.querySelectorAll('script[type="importmap"], script[type="importmap-shim"]'),
      );

      // Merge multiple import maps into one if needed
      if (mapScripts.length > 1) {
        const mergedImports: Record<string, string> = {};
        const mergedScopes: Record<string, Record<string, string>> = {};

        for (const script of mapScripts) {
          try {
            const parsed = JSON.parse((script.textContent || '').trim() || '{}');
            if (!parsed || typeof parsed !== 'object') continue;
            Object.assign(mergedImports, (parsed as any).imports || {});
            const scopes = (parsed as any).scopes || {};
            for (const [scopeKey, scopeMap] of Object.entries(scopes)) {
              if (!mergedScopes[scopeKey]) mergedScopes[scopeKey] = {};
              Object.assign(mergedScopes[scopeKey], (scopeMap as any) || {});
            }
          } catch {
            // Ignore malformed import maps; keep valid entries.
          }
        }

        const canonicalMapJson = JSON.stringify(
          {
            imports: mergedImports,
            ...(Object.keys(mergedScopes).length > 0 ? { scopes: mergedScopes } : {}),
          },
          null,
          2,
        );

        const first = mapScripts[0];
        first.setAttribute('type', 'importmap');
        first.textContent = canonicalMapJson;
        for (let i = 1; i < mapScripts.length; i++) {
          mapScripts[i].remove();
        }

        if (import.meta.env.DEV) {
          console.warn(`[preview] Merged ${mapScripts.length} import map tags into one canonical map`);
        }
      }

      // Proxy external images through our server to bypass COEP restrictions.
      // Servers like picsum.photos don't send CORP/CORS headers, so the browser
      // blocks them under COEP. Routing through /_api/imgproxy makes them same-origin.
      doc.querySelectorAll('img, video, audio, source').forEach((el) => {
        const attr = el.tagName === 'SOURCE' ? 'srcset' : 'src';
        const val = el.getAttribute(attr);
        if (val && /^https?:\/\//i.test(val)) {
          el.setAttribute(attr, toProxyUrl(val));
        } else if (val && /^\/_api\/imgproxy\?url=/i.test(val)) {
          const upstream = val.replace(/^\/_api\/imgproxy\?url=/i, '');
          try {
            el.setAttribute(attr, toProxyUrl(decodeURIComponent(upstream)));
          } catch {
            el.setAttribute(attr, `${proxyBase}${upstream}`);
          }
        }
        // Also handle srcset on img tags
        const srcset = el.getAttribute('srcset');
        if (srcset) {
          el.setAttribute(
            'srcset',
            srcset
              .replace(/(https?:\/\/[^\s,]+)/g, (url) => toProxyUrl(url))
              .replace(/\/_api\/imgproxy\?url=([^\s,]+)/gi, (_match, upstream) => {
                try {
                  return toProxyUrl(decodeURIComponent(upstream));
                } catch {
                  return `${proxyBase}${upstream}`;
                }
              }),
          );
        }
        el.removeAttribute('crossorigin');
      });

      // Also proxy CSS background-image URLs in inline styles
      doc.querySelectorAll('[style]').forEach((el) => {
        const style = el.getAttribute('style');
        if (style && /url\(\s*['"]?(?:https?:\/\/|\/_api\/imgproxy\?url=)/i.test(style)) {
          el.setAttribute('style', rewriteCssAssetUrls(style));
        }
      });

      // Also proxy external asset URLs inside <style> blocks.
      doc.querySelectorAll('style').forEach((styleEl) => {
        const cssText = styleEl.textContent || '';
        if (!/url\(\s*['"]?(?:https?:\/\/|\/_api\/imgproxy\?url=)/i.test(cssText)) return;
        styleEl.textContent = rewriteCssAssetUrls(cssText);
      });

      // Always return the modified doc (theme bridge + session theme + merged import maps)
      return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    } catch {
      return rawSrcDoc;
    }
  }, [rawSrcDoc, effectiveTheme, previewCustomThemeVars, activeSessionThemeStyle]);

  // ── Handlers ──
  const handleRefresh = () => {
    // Try to reload the actual iframe content first (works for URL-based previews)
    try {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.location.reload();
      }
    } catch {
      // Cross-origin iframe — fall through to key-based refresh
    }
    // Also bump the key to force React to remount the iframe element
    setRefreshKey((prev) => prev + 1);
    setIframeLoading(true);
    setIframeError(null);
    setRuntimeErrors([]);
    setPreviewReadySignal(false);
    lastRuntimeErrorMessageRef.current = null;
  };

  const handleIframeLoad = useCallback(() => {
    setIframeLoading(false);
    setIframeError(null);
    if (iframeLoadTimeoutRef.current) {
      clearTimeout(iframeLoadTimeoutRef.current);
      iframeLoadTimeoutRef.current = null;
    }
  }, []);

  const handleIframeError = useCallback(() => {
    setIframeLoading(false);
    // show an error message so the user isn't left staring at a blank/black preview
    setIframeError('Unable to load preview URL');
  }, []);

  const probeIframeDomContent = useCallback(
    (context: 'srcdoc' | 'url') => {
      const iframe = iframeRef.current;
      if (!iframe) return false;

      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        const body = doc?.body;
        if (!body) return false;

        const meaningfulElement = body.querySelector(
          'canvas,svg,img,video,audio,iframe,object,embed,main,section,article,[data-testid]',
        );
        const previewFallbackMarker = body.querySelector('[data-tasking-fallback]');
        const visibleChildren = Array.from(body.children).filter((child) => {
          const tag = child.tagName;
          return tag !== 'SCRIPT' && tag !== 'STYLE' && tag !== 'LINK' && tag !== 'META';
        });
        const nonTrivialChild = visibleChildren.length > 0;
        const looksLikePlaceholderRoot =
          visibleChildren.length === 1 &&
          ['DIV', 'MAIN', 'SECTION'].includes(visibleChildren[0].tagName) &&
          (visibleChildren[0].id === 'root' ||
            visibleChildren[0].id === 'app' ||
            visibleChildren[0].id === '__next' ||
            visibleChildren[0].className?.toString().includes('root')) &&
          (visibleChildren[0].textContent || '').trim().length === 0 &&
          visibleChildren[0].children.length === 0;
        const hasText = (body.textContent || '').trim().length > 0;

        // Detect Vite/WebContainer crash screen (shows a power/restart icon)
        const bodyHtml = body.innerHTML || '';
        const isViteCrashScreen = bodyHtml.includes('Restart') && bodyHtml.includes('Server') &&
          (body.querySelectorAll('svg').length > 0 || body.querySelectorAll('img[src*="restart"]').length > 0);
        if (isViteCrashScreen) {
          setIframeError('Vite dev server crashed. Click Retry to restart.');
          setPreviewReadySignal(false);
          return false;
        }

        const hasRenderableDom =
          Boolean(meaningfulElement) ||
          (nonTrivialChild && !looksLikePlaceholderRoot) ||
          hasText;

        if (context === 'srcdoc' && previewFallbackMarker) {
          const fallbackKind = String(
            previewFallbackMarker.getAttribute('data-tasking-fallback') || 'runtime-fallback',
          );
          const fallbackMessage =
            fallbackKind === 'non-renderable-object'
              ? 'Preview render failed: generated code passed a non-renderable object into React. Ask AI to return JSX elements/components rather than raw objects.'
              : 'Preview runtime fallback activated. The app did not mount through the normal preview runtime. Retry the preview, or ask AI to ensure the app mounts to #root and returns visible UI.';
          setIframeError(fallbackMessage);
          setPreviewReadySignal(false);
          return false;
        }

        if (context === 'srcdoc' && !hasRenderableDom) {
          setPreviewReadySignal(false);
          return false;
        }

        setIframeError(null);
        setPreviewReadySignal(true);
        return true;
      } catch {
        // Ignore cross-origin access errors for non-srcdoc iframes.
        return false;
      }
    },
    [buildErrors.length],
  );

  const handleSrcDocIframeLoad = useCallback(() => {
    handleIframeLoad();

    if (srcDocDomProbeTimeoutRef.current) {
      clearTimeout(srcDocDomProbeTimeoutRef.current);
    }
    srcDocDomProbeTimeoutRef.current = setTimeout(() => {
      let attempts = 0;
      const maxAttempts = 12; // Poll for up to ~14s total (past the 10s watchdog)
      const poll = () => {
        attempts += 1;
        const healthy = probeIframeDomContent('srcdoc');
        if (!healthy && attempts < maxAttempts) {
          srcDocDomProbeTimeoutRef.current = setTimeout(poll, 1000);
        } else if (!healthy) {
          setIframeError(
            'Preview rendered no visible output. Try Retry, or ask AI to ensure your app mounts to #root and returns JSX/UI elements.',
          );
        }
      };
      poll();
    }, 800);
  }, [handleIframeLoad, probeIframeDomContent]);

  const ensurePreviewInspector = useCallback((targetWindow?: Window | null) => {
    if (!targetWindow) return false;

    try {
      const doc = targetWindow.document;
      if (!doc?.documentElement) return false;

      if (!doc.querySelector('script[data-tasking-preview-inspector="true"]')) {
        const script = doc.createElement('script');
        script.type = 'text/javascript';
        script.dataset.taskingPreviewInspector = 'true';
        script.text = PREVIEW_INSPECTOR_SCRIPT_CONTENT;
        (doc.head || doc.body || doc.documentElement).appendChild(script);
      }

      return true;
    } catch {
      return false;
    }
  }, []);

  const handleUrlIframeLoad = useCallback(() => {
    handleIframeLoad();

    const previewWindow = iframeRef.current?.contentWindow;
    const inspectorReady = ensurePreviewInspector(previewWindow);

    if (inspectorReady && effectiveInspectMode) {
      previewWindow?.postMessage({ type: 'inspect-mode', enabled: true }, '*');
    }

    if (srcDocDomProbeTimeoutRef.current) {
      clearTimeout(srcDocDomProbeTimeoutRef.current);
    }
    srcDocDomProbeTimeoutRef.current = setTimeout(() => {
      probeIframeDomContent('url');
    }, 1200);
  }, [effectiveInspectMode, ensurePreviewInspector, handleIframeLoad, probeIframeDomContent]);

  // Listen for route-change messages from the iframe (when user clicks links inside preview)
  useEffect(() => {
    previewReadySignalRef.current = previewReadySignal;
  }, [previewReadySignal]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const previewWindow = iframeRef.current?.contentWindow;
      if (previewWindow && e.source !== previewWindow) return;

      if (e.data && e.data.type === 'route-change' && e.data.path) {
        const normalized = e.data.path.startsWith('/') ? e.data.path : `/${e.data.path}`;
        setInternalSelectedPage(normalized);
        if (onPageChange) onPageChange(normalized);
        return;
      }

      // External link clicked inside preview — open in a new tab (prevents inception)
      if (e.data && e.data.type === 'open-external' && e.data.url) {
        try {
          window.open(String(e.data.url), '_blank', 'noopener,noreferrer');
        } catch (_) {}
        return;
      }

      if (e.data && e.data.type === 'preview-ready') {
        if (srcDoc) {
          const healthy = probeIframeDomContent('srcdoc');
          if (!healthy) return;
        }
        setPreviewReadySignal(true);
        setIframeError(null);
        // Preview is healthy — clear any lingering error state silently
        setRuntimeErrors([]);
        return;
      }

      if (e.data && e.data.type === 'preview-dom-populated') {
        if (srcDoc) {
          const healthy = probeIframeDomContent('srcdoc');
          if (!healthy) return;
        }
        setPreviewReadySignal(true);
        setIframeError(null);
        return;
      }

      if (e.data && e.data.type === 'element-selected' && e.data.element) {
        setInspectMode(false);
        const msg = buildInspectContext(e.data.element);
        window.dispatchEvent(new CustomEvent('tasking.prefillChat', { detail: { message: msg } }));
        return;
      }

      if (e.data && e.data.type === 'inspect-mode-off') {
        setInspectMode(false);
        return;
      }

      if (e.data && e.data.type === 'element-action') {
        const { action, element, imageData } = e.data;

        // Find which file likely contains this element
        const targetFile = sessionFilesRef.current.find((f) => {
          const ext = f.fileName.split('.').pop()?.toLowerCase() || '';
          return ['tsx', 'jsx', 'ts', 'js', 'html'].includes(ext) && f.content;
        });

        if (!targetFile) {
          toast.error('Could not locate file for this element');
          return;
        }

        // Build context for AI
        const elementContext = `I want to modify an element in the preview:
- Tag: <${element.tag}${element.id ? ` id="${element.id}"` : ''}${element.classes ? ` class="${element.classes}"` : ''}>
- Text content: "${element.text || '(empty)'}"
- Size: ${element.rect.width}×${element.rect.height}px
- Location: ${element.path}
${action === 'upload' ? `- I've uploaded an image to replace it with` : `- I want to regenerate/redesign this element`}`;

        // Store pending change with original content for undo support
        setElementChange({
          element,
          action,
          imageData,
          originalFile: targetFile,
          originalContent: targetFile.content || '',
        });

        // Emit event that parent can listen to - parent handles sending to AI chat
        window.dispatchEvent(
          new CustomEvent('tasking.elementEditRequest', {
            detail: { element, action, imageData, elementContext, sessionId },
          }),
        );

        toast.info('Element selected - provide changes in the chat');
        return;
      }

      if (e.data && e.data.type === 'preview-runtime-error') {
        const kind = String(e.data.kind || 'runtime');
        const details = e.data.details || {};
        const message = String(details.message || 'Unknown runtime error');
        lastRuntimeErrorMessageRef.current = message;

        const isRenderablePayloadError =
          /Objects are not valid as a React child/i.test(message) ||
          /Minified React error\s*#31/i.test(message) ||
          /non-renderable object payload/i.test(message);
        if (isRenderablePayloadError) {
          const summary = typeof details.summary === 'string' ? details.summary.trim() : '';
          const preview = typeof details.preview === 'string' ? details.preview.trim() : '';
          const extra = [summary, preview].filter(Boolean).join('\n\n');
          setIframeError(
            extra
              ? `Preview render failed: generated code passed a non-renderable object into React.\n\n${extra}`
              : 'Preview render failed: generated code passed a non-renderable object into React. Ask AI to return JSX elements/components, not raw objects.',
          );
          setRuntimeErrors(extra ? [message, extra] : [message]);
          return;
        }

        // ── Silent suppression: hide errors caused by preview environment, not user code ──
        // These are infrastructure errors (dual-React instances, bare specifier failures,
        // missing module resolution in blob URL context, etc.) that users should never see.
        const suppressPatterns = [
          /is not a function/i, // dual-React: useState/useEffect etc.
          /Cannot read properties of (?:undefined|null)/i, // cascading errors from failed imports
          /Failed to resolve module specifier/i, // bare specifier in blob URL
          /Minified React error/i, // production React error messages
          /Loading chunk.*failed/i, // code splitting in preview
          /Cannot use import statement/i, // CJS/ESM mismatch
          /Unexpected token 'export'/i, // CJS/ESM mismatch
          /process is not defined/i, // Node.js global in browser
          /require is not (?:defined|a function)/i, // CJS require in browser
          /module is not defined/i, // CJS module in browser
          /global is not defined/i, // Node.js global in browser
          /Buffer is not defined/i, // Node.js Buffer in browser
          /window is not defined/i, // SSR code in browser preview
          /document is not defined/i, // SSR code in browser preview
          /navigator is not defined/i, // SSR code in browser preview
          /ResizeObserver loop/i, // Harmless ResizeObserver warning
          /Script error\.?$/i, // Cross-origin script error (no info)
          /hydrat/i, // React hydration mismatch warnings
          /Unexpected token/i, // Parse errors from stale cache
          /Failed to fetch/i, // Network errors in preview context
          /Load failed/i, // Resource load errors
          /AbortError/i, // Aborted fetches
          /ChunkLoadError/i, // Webpack chunk loading
          /dynamically imported module/i, // Dynamic import failures in preview
          /MIME type/i, // MIME type mismatches in blob/data URLs
          /Blocked a frame with origin/i, // Cross-origin frame blocking
          /SecurityError/i, // Cross-origin/srcdoc security
          /__webpack/i, // Webpack internals in browser
          /hot.*module.*replacement/i, // HMR errors in preview
          /Cannot access.*before initialization/i, // TDZ errors in bundled code
        ];
        // ── ZERO RESTRICTIONS: All runtime errors are suppressed from UI ──
        // Users should see their preview no matter what. Errors go to console only.
        // The auto-fix system still runs silently in the background.
        console.debug(`[preview] Runtime error (suppressed from UI): ${message.slice(0, 200)}`);

        // If runtime errors are happening and srcdoc still has no renderable DOM,
        // surface an actionable overlay instead of leaving a silent black frame.
        if (srcDoc) {
          setTimeout(() => {
            const healthy = probeIframeDomContent('srcdoc');
            if (!healthy) {
              setIframeError(
                'Preview runtime failed before rendering. Try Retry, or ask AI to fix runtime errors and ensure your app mounts to #root.',
              );
            }
          }, 800);
        }

        // ── Silent auto-fix: intercept "X is not defined" and fix imports in source files ──
        const notDefinedMatch = message.match(/^(\w+) is not defined$/);
        if (notDefinedMatch) {
          const compName = notDefinedMatch[1];
          if (/^[A-Z]/.test(compName)) {
            const attempts = _autofixAttempts.get(`${sessionId}:${compName}`) || 0;
            if (attempts < autoFixMaxRetries && !_autofixInFlight.has(`${sessionId}:${compName}`)) {
              _autofixInFlight.add(`${sessionId}:${compName}`);
              _autofixAttempts.set(`${sessionId}:${compName}`, attempts + 1);

              // Find which file(s) use this symbol but don't import it
              const currentFiles = sessionFilesRef.current;
              const candidateFiles = currentFiles.filter((f) => {
                if (!f.content) return false;
                const ext = f.fileName.split('.').pop()?.toLowerCase() || '';
                if (!['tsx', 'jsx', 'ts', 'js'].includes(ext)) return false;
                // File uses the symbol but doesn't import it
                return (
                  f.content.includes(compName) &&
                  !new RegExp(`import\\s.*\\b${compName}\\b.*from`).test(f.content)
                );
              });

              const targetFile =
                candidateFiles[0] ||
                currentFiles.find(
                  (f) => /\.(tsx|jsx)$/.test(f.fileName) && f.content?.includes(compName),
                );

              if (targetFile) {
                const fullPath =
                  targetFile.filePath === '/' || !targetFile.filePath
                    ? targetFile.fileName
                    : `${targetFile.filePath.replace(/^\//, '').replace(/\/$/, '')}/${targetFile.fileName}`;

                autoFixComponent({
                  sessionId,
                  filePath: fullPath,
                  fileContents: targetFile.content || '',
                  errorMessage: message,
                  componentName: compName,
                  framework: detectedWebFramework,
                  existingFileNames: currentFiles.map((f) => f.fileName),
                })
                  .then((result) => {
                    if (!result || !result.patches?.length) return;
                    // Apply patches to the file
                    let newContent = targetFile.content || '';
                    for (const patch of result.patches) {
                      if (patch.before === '') {
                        newContent = patch.after + newContent;
                      } else {
                        newContent = newContent.replace(patch.before, () => patch.after);
                      }
                    }
                    // Save the patched file
                    return createFile({
                      sessionId,
                      fileName: targetFile.fileName,
                      filePath: targetFile.filePath || '/',
                      content: newContent,
                    }).then(() => {
                      console.debug(`[autofix] Patched ${targetFile.fileName}: ${result.summary}`);
                      setRuntimeErrors([]);
                      // Cooldown: skip invalidation if one happened < 3s ago (prevents infinite loop)
                      const now = Date.now();
                      if (now - lastAutofixInvalidationRef.current > 3000) {
                        lastAutofixInvalidationRef.current = now;
                        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
                      }
                    });
                  })
                  .catch((err) => {
                    console.debug(`[autofix] Failed to auto-fix ${compName}:`, err);
                  })
                  .finally(() => {
                    _autofixInFlight.delete(`${sessionId}:${compName}`);
                  });
              } else {
                _autofixInFlight.delete(`${sessionId}:${compName}`);
              }
            }
          }
        }
        // No runtime errors are ever shown to the user — the preview just runs
      }

      // ── preview-component-missing: bootstrap stubbed PascalCase components → autofix them ──
      if (e.data && e.data.type === 'preview-component-missing') {
        const missingComponents: string[] = Array.isArray(e.data.components)
          ? e.data.components
          : [];
        // Dynamically build the skip set: any component that already has a matching
        // file in the session (e.g. About.tsx, Header.tsx) should NOT trigger autofix —
        // the file exists, so stubbing means the bundler/import failed, not that the
        // component needs to be generated from scratch. Also skip known UI library components.
        const currentFiles = sessionFilesRef.current;
        const fileDefinedComponents = new Set<string>();
        for (const f of currentFiles) {
          // Extract component name from filename: "About.tsx" → "About", "components/Header.tsx" → "Header"
          const baseName = f.fileName.replace(/\.(tsx|jsx|ts|js)$/, '');
          if (/^[A-Z]/.test(baseName)) fileDefinedComponents.add(baseName);
          // Also check if file content exports the component name
          if (f.content) {
            const exportMatch = f.content.match(/export\s+(?:default\s+)?(?:function|class|const)\s+([A-Z][A-Za-z0-9]*)/g);
            if (exportMatch) {
              for (const em of exportMatch) {
                const nameMatch = em.match(/([A-Z][A-Za-z0-9]*)$/);
                if (nameMatch) fileDefinedComponents.add(nameMatch[1]);
              }
            }
          }
        }
        // Static UI library components that should never trigger autofix
        const UI_LIB_COMPONENTS = new Set([
          'Button', 'Card', 'CardHeader', 'CardTitle', 'CardDescription',
          'CardContent', 'CardFooter', 'Input', 'Label', 'Badge', 'Container',
          'Separator', 'Textarea', 'Skeleton', 'Avatar', 'AvatarImage',
          'AvatarFallback', 'Checkbox', 'Dialog', 'DialogOverlay', 'DialogContent',
          'DialogHeader', 'DialogFooter', 'DialogTitle', 'DialogDescription',
          'Tabs', 'TabsList', 'TabsTrigger', 'TabsContent',
          'ThemeProvider', 'ThemeToggle', 'ToastContainer', 'ToastProvider',
        ]);
        console.debug('[autofix] fileDefinedComponents:', [...fileDefinedComponents], 'missing:', missingComponents, 'sessionFiles:', currentFiles.length);
        for (const compName of missingComponents) {
          if (!/^[A-Z]/.test(compName)) continue;
          const baseName = compName.replace(/\d+$/, '');
          // Skip if a file with this component name exists in the session
          if (fileDefinedComponents.has(compName) || fileDefinedComponents.has(baseName)) {
            console.debug(`[autofix] Skipping "${compName}" — file exists in session`);
            continue;
          }
          // Skip UI library components
          if (UI_LIB_COMPONENTS.has(compName)) continue;
          if (baseName !== compName && UI_LIB_COMPONENTS.has(baseName)) continue;
          // Skip Lucide icons
          const lucide = (globalThis as any).__TASKING_Lucide || (globalThis as any).lucide;
          if (lucide && lucide[compName]) continue;
          if (baseName !== compName && lucide && lucide[baseName]) continue;

          const attempts = _autofixAttempts.get(`${sessionId}:${compName}`) || 0;
          if (attempts >= autoFixMaxRetries || _autofixInFlight.has(`${sessionId}:${compName}`)) continue;

          _autofixInFlight.add(`${sessionId}:${compName}`);
          _autofixAttempts.set(`${sessionId}:${compName}`, attempts + 1);

          // Find a file that uses this component name
          const missingCurrentFiles = sessionFilesRef.current;
          const candidateFiles = missingCurrentFiles.filter((f) => {
            if (!f.content) return false;
            const ext = f.fileName.split('.').pop()?.toLowerCase() || '';
            if (!['tsx', 'jsx', 'ts', 'js'].includes(ext)) return false;
            return (
              f.content.includes(compName) &&
              !new RegExp(`import\\s.*\\b${compName}\\b.*from`).test(f.content)
            );
          });

          const targetFile =
            candidateFiles[0] ||
            missingCurrentFiles.find(
              (f) => /\.(tsx|jsx)$/.test(f.fileName) && f.content?.includes(compName),
            );

          if (!targetFile) {
            _autofixInFlight.delete(`${sessionId}:${compName}`);
            continue;
          }

          const fullPath =
            targetFile.filePath === '/' || !targetFile.filePath
              ? targetFile.fileName
              : `${targetFile.filePath.replace(/^\//, '').replace(/\/$/, '')}/${targetFile.fileName}`;

          console.debug(
            `[autofix] Bootstrap-stubbed component "${compName}" → generating from ${targetFile.fileName}`,
          );

          autoFixComponent({
            sessionId,
            filePath: fullPath,
            fileContents: targetFile.content || '',
            errorMessage: `${compName} is not defined`,
            componentName: compName,
            framework: detectedWebFramework,
            existingFileNames: missingCurrentFiles.map((f) => f.fileName),
          })
            .then((result) => {
              if (!result || !result.patches?.length) return;
              let newContent = targetFile.content || '';
              for (const patch of result.patches) {
                if (patch.before === '') {
                  newContent = patch.after + newContent;
                } else {
                  newContent = newContent.replace(patch.before, () => patch.after);
                }
              }
              return createFile({
                sessionId,
                fileName: targetFile.fileName,
                filePath: targetFile.filePath || '/',
                content: newContent,
              }).then(() => {
                console.debug(
                  `[autofix] Generated missing component "${compName}" in ${targetFile.fileName}`,
                );
                // Cooldown: skip invalidation if one happened < 3s ago (prevents infinite loop)
                const now = Date.now();
                if (now - lastAutofixInvalidationRef.current > 3000) {
                  lastAutofixInvalidationRef.current = now;
                  queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
                }
              });
            })
            .catch((err) => {
              console.debug(`[autofix] Failed to auto-fix missing component ${compName}:`, err);
            })
            .finally(() => {
              _autofixInFlight.delete(`${sessionId}:${compName}`);
            });
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onPageChange, sessionId, detectedWebFramework]);
  // NOTE: sessionFiles intentionally removed from deps — accessed via sessionFilesRef
  // to prevent infinite loop: autofix → invalidateQueries → sessionFiles refetch →
  // handler re-registers → bundling effect → errors → autofix again (React error #185)

  // Detect silent black/blank srcdoc previews and force an actionable overlay.
  useEffect(() => {
    if (!srcDoc) {
      setPreviewReadySignal(false);
      previewReadySignalRef.current = false;
      if (srcDocHealthTimeoutRef.current) {
        clearTimeout(srcDocHealthTimeoutRef.current);
        srcDocHealthTimeoutRef.current = null;
      }
      if (srcDocDomProbeTimeoutRef.current) {
        clearTimeout(srcDocDomProbeTimeoutRef.current);
        srcDocDomProbeTimeoutRef.current = null;
      }
      return;
    }

    setPreviewReadySignal(false);
    previewReadySignalRef.current = false;
    if (srcDocHealthTimeoutRef.current) {
      clearTimeout(srcDocHealthTimeoutRef.current);
    }

    srcDocHealthTimeoutRef.current = setTimeout(() => {
      const healthy = probeIframeDomContent('srcdoc');
      if (!healthy && buildErrors.length === 0) {
        const runtimeHint = lastRuntimeErrorMessageRef.current
          ? ` Last runtime error: ${lastRuntimeErrorMessageRef.current.slice(0, 180)}`
          : '';
        setIframeError(
          runtimeHint
            ? `App crashed: ${lastRuntimeErrorMessageRef.current?.slice(0, 200)}. Ask AI to fix this error.`
            : `Preview did not render visible content. Try Retry, or ask AI to fix the code.`,
        );
      }
    }, 6000);

    return () => {
      if (srcDocHealthTimeoutRef.current) {
        clearTimeout(srcDocHealthTimeoutRef.current);
        srcDocHealthTimeoutRef.current = null;
      }
      if (srcDocDomProbeTimeoutRef.current) {
        clearTimeout(srcDocDomProbeTimeoutRef.current);
        srcDocDomProbeTimeoutRef.current = null;
      }
    };
  }, [srcDoc, refreshKey, buildErrors.length, probeIframeDomContent]);

  // Timeout for iframe loading when using published URL
  useEffect(() => {
    if (effectivePreviewUrl && iframeLoading) {
      if (iframeLoadTimeoutRef.current) clearTimeout(iframeLoadTimeoutRef.current);
      // give up after 30s and report a timeout so the user can retry/open in new tab
      iframeLoadTimeoutRef.current = setTimeout(() => {
        if (iframeLoading) {
          setIframeLoading(false);
          setIframeError('Preview load timed out');
        }
      }, 30000);
    }
    return () => {
      if (iframeLoadTimeoutRef.current) clearTimeout(iframeLoadTimeoutRef.current);
    };
  }, [effectivePreviewUrl, iframeLoading, refreshKey]);

  useEffect(() => {
    if (effectivePreviewUrl) {
      setIframeLoading(true);
      setIframeError(null);
    }
  }, [effectivePreviewUrl]);

  const handlePublish = () => {
    setIsPublishDialogOpen(true);
  };

  // Shared helper: capture iframe content using html2canvas
  const captureIframeContent = async (): Promise<string | null> => {
    const iframe = iframeRef.current;
    if (!iframe) return null;

    const rect = iframe.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    // Pre-process: convert all <img> in the iframe to inline data URLs
    // so html2canvas can render them (srcdoc relative paths are unresolvable)
    const inlineImages = async (doc: Document) => {
      const imgs = doc.querySelectorAll('img');
      const promises = Array.from(imgs).map(async (img) => {
        if (!img.src || img.src.startsWith('data:')) return;
        // Skip already-failed images
        if (!img.naturalWidth && !img.complete) return;
        try {
          // Draw the already-loaded image to a canvas to get a data URL
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            const c = doc.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            const ctx = c.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0);
              try {
                img.src = c.toDataURL('image/png');
              } catch {
                // Tainted canvas — try fetching the image directly
              }
            }
          }
        } catch {
          // Image couldn't be inlined — will show as broken in capture
        }
      });
      await Promise.all(promises);
    };

    // Helper to attempt html2canvas capture
    const attemptCapture = async (): Promise<string | null> => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc?.body) return null;
      // Inline images before capture
      await inlineImages(iframeDoc);
      const canvas = await html2canvas(iframeDoc.body, {
        useCORS: true,
        allowTaint: true,
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        backgroundColor: '#1a1a2e',
        scale: window.devicePixelRatio,
        logging: false,
        foreignObjectRendering: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrl && dataUrl !== 'data:,') return dataUrl;
      return null;
    };

    // Attempt 1: immediate capture
    try {
      const result = await attemptCapture();
      if (result) return result;
    } catch (e) {
      console.warn('[Screenshot] html2canvas attempt 1 failed:', e);
    }

    // Attempt 2: wait for iframe to paint, then retry
    await new Promise((r) => setTimeout(r, 300));
    try {
      const result = await attemptCapture();
      if (result) return result;
    } catch (e) {
      console.warn('[Screenshot] html2canvas attempt 2 failed:', e);
    }

    // Attempt 3: try without foreignObject rendering
    await new Promise((r) => setTimeout(r, 200));
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc?.body) {
        await inlineImages(iframeDoc);
        const canvas = await html2canvas(iframeDoc.body, {
          useCORS: true,
          allowTaint: true,
          width,
          height,
          windowWidth: width,
          windowHeight: height,
          backgroundColor: '#1a1a2e',
          scale: window.devicePixelRatio,
          logging: false,
          foreignObjectRendering: false,
        });
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl && dataUrl !== 'data:,') return dataUrl;
      }
    } catch (e) {
      console.warn('[Screenshot] html2canvas attempt 3 failed:', e);
    }

    // Fallback: return null (caller draws placeholder)
    return null;
  };

  const handleScreenshot = async () => {
    if (!onScreenshot || isCapturing) return;
    setIsCapturing(true);

    try {
      const dataUrl = await captureIframeContent();
      if (dataUrl) {
        onScreenshot(dataUrl);
        toast.success('Screenshot captured!');
      } else {
        // Fallback placeholder
        const iframe = iframeRef.current;
        const rect = iframe?.getBoundingClientRect();
        const width = Math.round(rect?.width || 800);
        const height = Math.round(rect?.height || 600);
        const canvas = document.createElement('canvas');
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        const ctx = canvas.getContext('2d')!;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Screenshot capture unavailable', width / 2, height / 2 - 10);
        ctx.font = '12px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#ff7f50';
        ctx.fillText('Draw annotations to point out UI elements', width / 2, height / 2 + 20);
        onScreenshot(canvas.toDataURL('image/png'));
      }
    } catch (err) {
      console.error('Screenshot capture failed:', err);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleAnnotate = async () => {
    if (isCapturing) return;
    setIsCapturing(true);

    try {
      const dataUrl = await captureIframeContent();
      if (dataUrl) {
        setAnnotationScreenshot(dataUrl);
        setIsAnnotatingInline(true);
      } else {
        // Fallback placeholder - still open annotation overlay
        const iframe = iframeRef.current;
        const rect = iframe?.getBoundingClientRect();
        const width = Math.round(rect?.width || 800);
        const height = Math.round(rect?.height || 600);
        const canvas = document.createElement('canvas');
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        const ctx = canvas.getContext('2d')!;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Preview Screenshot', width / 2, height / 2 - 10);
        ctx.font = '12px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#ff7f50';
        ctx.fillText('Draw annotations to point out UI elements', width / 2, height / 2 + 20);
        setAnnotationScreenshot(canvas.toDataURL('image/png'));
        setIsAnnotatingInline(true);
      }
    } catch (err) {
      console.error('Annotation capture failed:', err);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleInlineAnnotationSave = (data: any) => {
    setIsAnnotatingInline(false);
    setAnnotationScreenshot(null);
    // Pass annotated image + text notes to parent via onAnnotate
    if (onAnnotate && data?.annotatedImage) {
      // Extract text notes from annotation data so AI can read them
      const textNotes: string[] = [];
      if (data.annotationData?.textNotes?.length) {
        for (const note of data.annotationData.textNotes) {
          if (note.text?.trim()) textNotes.push(note.text.trim());
        }
      }
      if (data.description?.trim()) {
        textNotes.unshift(data.description.trim());
      }
      onAnnotate(data.annotatedImage, textNotes);
    }
  };

  const handleInlineAnnotationCancel = () => {
    setIsAnnotatingInline(false);
    setAnnotationScreenshot(null);
  };

  const handleToggleInspect = useCallback(() => {
    const next = !effectiveInspectMode;
    ensurePreviewInspector(iframeRef.current?.contentWindow);
    setInspectMode(next);
    iframeRef.current?.contentWindow?.postMessage({ type: 'inspect-mode', enabled: next }, '*');
  }, [effectiveInspectMode, ensurePreviewInspector, setInspectMode]);

  const handleToggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (panelRef.current?.requestFullscreen) {
        panelRef.current.requestFullscreen().catch(() => setIsFullscreen(true));
      } else {
        setIsFullscreen(true);
      }
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => setIsFullscreen(false));
      } else {
        setIsFullscreen(false);
      }
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Close inspect mode with ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && effectiveInspectMode) {
        e.preventDefault();
        setInspectMode(false);
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'inspect-mode', enabled: false },
          '*',
        );
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [effectiveInspectMode, setInspectMode]);

  // Register screenshot function with parent ref so AI chat can trigger it
  useEffect(() => {
    if (screenshotTriggerRef) {
      screenshotTriggerRef.current = handleScreenshot;
    }
    return () => {
      if (screenshotTriggerRef) {
        screenshotTriggerRef.current = null;
      }
    };
  }, [handleScreenshot, screenshotTriggerRef]);

  // Godot-specific handlers
  const handleDownloadGodotProject = useCallback(async () => {
    if (sessionFiles.length === 0) return;
    setIsDownloading(true);
    try {
      const projectGodot = sessionFiles.find((f) => f.fileName === 'project.godot');
      let projectName = 'godot-project';
      if (projectGodot?.content) {
        const match = projectGodot.content.match(/config\/name="([^"]+)"/);
        if (match) projectName = match[1].replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
      }
      await downloadFilesAsZip(sessionFiles, projectName);
    } catch (err) {
      console.error('Failed to download project:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [sessionFiles]);

  const handleOpenGodotWebEditor = useCallback(() => {
    window.open(GODOT_WEB_EDITOR_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const handleLaunchGodotEditorEmbed = useCallback(() => {
    setShowGodotEditor(true);
  }, []);

  const handleDownloadAsset = useCallback(async (file: Selectable<SessionFiles>) => {
    try {
      const content = file.content || '';
      let blob: Blob;

      if (content.startsWith('data:')) {
        const response = await fetch(content);
        blob = await response.blob();
      } else {
        blob = new Blob([content], { type: getAssetMimeType(file.fileName) });
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download asset:', err);
      toast.error('Could not download this asset');
    }
  }, []);

  // ── Render functions ──

  const renderPreviewContent = () => {
    // Unreal Engine projects - show download instructions (no web preview)
    if (isUnrealProject) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏗️</div>
            <h3>Unreal Engine Project</h3>
            <p className={styles.subtext}>
              This is an Unreal Engine project.
              <br />
              <br />
              To preview: Download the project files and open in Unreal Editor. Build and run to see
              your game in action.
            </p>
            <Button
              onClick={() => downloadFilesAsZip(sessionFiles, 'unreal-project')}
              disabled={isDownloading || sessionFiles.length === 0}
              variant="primary"
              size="md"
            >
              <Download size={16} />
              {isDownloading ? 'Downloading...' : 'Download Unreal Project'}
            </Button>
          </div>
        </div>
      );
    }

    // Mobile shell projects - show download instructions instead of preview
    if (isMobileShellProject) {
      const platform =
        templateId === 'android-shell'
          ? 'Android'
          : templateId === 'ios-shell'
            ? 'iOS'
            : 'Android + iOS';
      const ide =
        templateId === 'android-shell'
          ? 'Android Studio'
          : templateId === 'ios-shell'
            ? 'Xcode'
            : 'Android Studio or Xcode';
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📱</div>
            <h3>{platform} App Shell</h3>
            <p className={styles.subtext}>
              This template creates a {platform.toLowerCase()} WebView wrapper project.
              <br />
              <br />
              To preview: Download the project files and open in {ide}. Build the app to see your
              web content in the native WebView.
            </p>
            <Button
              onClick={() =>
                downloadFilesAsZip(
                  sessionFiles,
                  `${templateId?.replace('-shell', '') || 'mobile'}-project`,
                )
              }
              disabled={isDownloading || sessionFiles.length === 0}
              variant="primary"
              size="md"
            >
              <Download size={16} />
              {isDownloading ? 'Downloading...' : `Download ${platform} Project`}
            </Button>
          </div>
        </div>
      );
    }

    // WebContainer Vite preview (URL-based iframe) — preferred for bundleable projects
    if (webContainerUrl && requiresBundling) {
      return (
        <div className={`${styles.frameWrapper} ${styles[deviceSize]}`}>
          {isBuilding && (
            <div className={styles.buildingOverlay}>
              <Loader2 size={16} className={styles.buildingSpinner} />
              Building…
            </div>
          )}
          <iframe
            key={refreshKey}
            ref={iframeRef}
            src={webContainerUrl}
            className={styles.iframe}
            title="Live Preview"
            allow="cross-origin-isolated"
            onError={handleIframeError}
          />
          {(iframeError || buildErrors.length > 0) && (
            <div className={styles.iframeErrorOverlay}>
              <div className={styles.iframeErrorContent}>
                <img src="/icons/restart.png" alt="Restart" style={{ height: 64, width: 'auto', opacity: 0.7 }} />
                <div className={styles.iframeErrorTitle}>Preview failed</div>
                <div className={styles.iframeErrorMessage}>
                  {iframeError || buildErrors.join('\n')}
                </div>
                <div className={styles.iframeErrorActions}>
                  <Button size="sm" variant="outline" onClick={handleRefresh}>
                    <RefreshCw size={14} />
                    Retry
                  </Button>
                  {onTryFix && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        onTryFix(
                          iframeError || buildErrors.join('\n') || 'Preview build failed'
                        )
                      }
                    >
                      <Wrench size={14} />
                      Ask AI to Fix
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Published URL iframe (Worker Pages / deployed preview)
    // For non-bundleable web templates, prefer URL preview when available.
    if (effectivePreviewUrl && (!srcDoc || shouldUseWorkerPagesPreview)) {
      const iframeProps: any = {
        key: refreshKey,
        ref: iframeRef,
        className: styles.iframe,
        title: 'Live Preview',
        onLoad: handleUrlIframeLoad,
        onError: handleIframeError,
        src: effectivePreviewUrl,
      };

      // show overlay for load errors/timeouts so the user isn't left with a black box
      return (
        <div className={`${styles.frameWrapper} ${styles[deviceSize]}`}>
          {iframeLoading && (
            <div className={styles.iframeLoadingOverlay}>
              <Loader2 size={32} className={styles.spin} />
              <div>Loading preview...</div>
            </div>
          )}
          {iframeError && (
            <div className={styles.iframeErrorOverlay}>
              <div className={styles.iframeErrorContent}>
                <img src="/icons/restart.png" alt="Restart" style={{ height: 64, width: 'auto', opacity: 0.7 }} />
                <div className={styles.iframeErrorTitle}>Preview failed</div>
                <div className={styles.iframeErrorMessage}>{iframeError}</div>
                <div className={styles.iframeErrorActions}>
                  <Button size="sm" variant="outline" onClick={handleRefresh}>
                    Retry
                  </Button>
                  {effectivePreviewUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(effectivePreviewUrl, '_blank', 'noopener')}
                    >
                      Open in new tab
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Runtime errors: NEVER shown — zero restrictions */}
          <iframe {...iframeProps} />
        </div>
      );
    }

    // Godot project handling
    if (isGodotProject && isNonWebProject) {
      if (showGodotEditor) {
        return (
          <div
            className={styles.frameWrapper}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                display: 'flex',
                gap: '8px',
                padding: '8px',
                background: 'var(--muted)',
                borderBottom: '1px solid var(--border)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: 500, marginRight: 'auto' }}>
                <GamepadIcon
                  size={14}
                  style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}
                />
                Godot Web Editor
              </span>
              <Button
                onClick={handleDownloadGodotProject}
                variant="outline"
                size="sm"
                disabled={isDownloading || sessionFiles.length === 0}
              >
                <Download size={12} />
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
              <Button onClick={handleOpenGodotWebEditor} variant="outline" size="sm">
                <ExternalLink size={12} />
                New Tab
              </Button>
            </div>
            <iframe
              src={GODOT_WEB_EDITOR_URL}
              className={styles.iframe}
              title="Godot Web Editor"
              allow="cross-origin-isolated; fullscreen"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-top-navigation allow-popups-to-escape-sandbox"
              style={{ width: '100%', flex: 1, border: 'none' }}
            />
          </div>
        );
      }

      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <GamepadIcon size={48} />
            <div>Godot Project Detected</div>
            <div className={styles.subtext}>
              Godot projects require the Godot Editor to run. Use the options below.
            </div>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginTop: '16px',
              }}
            >
              <Button onClick={handleLaunchGodotEditorEmbed} variant="primary">
                <GamepadIcon size={16} />
                Open Godot Web Editor
              </Button>
              <Button onClick={handleOpenGodotWebEditor} variant="outline">
                <ExternalLink size={16} />
                Open in New Tab
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginTop: '12px',
              }}
            >
              <Button
                onClick={handleDownloadGodotProject}
                variant="outline"
                disabled={isDownloading || sessionFiles.length === 0}
              >
                <Download size={16} />
                {isDownloading ? 'Downloading...' : 'Download Project'}
              </Button>
              {onImportFiles && (
                <Button onClick={onImportFiles} variant="outline">
                  <Upload size={16} />
                  Import Files
                </Button>
              )}
              {onImportFromGitHub && (
                <Button onClick={onImportFromGitHub} variant="outline">
                  <Github size={16} />
                  Import from GitHub
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Non-web projects
    if (isNonWebProject) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <AlertCircle size={48} />
            <div>Preview not available</div>
            <div className={styles.subtext}>
              This project type doesn&apos;t support web preview.
            </div>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginTop: '16px',
              }}
            >
              <Button
                onClick={() => downloadFilesAsZip(sessionFiles, 'project')}
                variant="outline"
                disabled={sessionFiles.length === 0}
              >
                <Download size={16} />
                Download Project
              </Button>
              {onImportFiles && (
                <Button onClick={onImportFiles} variant="outline">
                  <Upload size={16} />
                  Import Files
                </Button>
              )}
              {onImportFromGitHub && (
                <Button onClick={onImportFromGitHub} variant="outline">
                  <Github size={16} />
                  Import from GitHub
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Media-only generation fallback:
    // when the current HTML is only a placeholder mount shell, render the first image
    // so users immediately see generated assets instead of a blank/black frame.
    if (ENABLE_MEDIA_ONLY_FALLBACK && shouldShowMediaOnlyFallback && mediaOnlyFallbackSrc) {
      return (
        <div className={`${styles.frameWrapper} ${styles[deviceSize]}`}>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--muted)',
              padding: '18px',
              boxSizing: 'border-box',
            }}
          >
            <img
              src={mediaOnlyFallbackSrc}
              alt={imageFiles[0]?.fileName || 'Generated asset'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.35)',
              }}
            />
          </div>
        </div>
      );
    }

    // Static HTML srcDoc preview (naive fallback for non-bundled projects)
    if (srcDoc) {
      return (
        <div className={`${styles.frameWrapper} ${styles[deviceSize]}`}>
          {isBuilding && (
            <div className={styles.buildingOverlay}>
              <Loader2 size={16} className={styles.buildingSpinner} />
              Building…
            </div>
          )}
          {iframeError && (
            <div className={styles.iframeErrorOverlay}>
              <div className={styles.iframeErrorContent}>
                <img src="/icons/restart.png" alt="Restart" style={{ height: 64, width: 'auto', opacity: 0.7 }} />
                <div className={styles.iframeErrorTitle}>Preview failed</div>
                <div className={styles.iframeErrorMessage}>{iframeError}</div>
                <div className={styles.iframeErrorActions}>
                  <Button size="sm" variant="outline" onClick={handleRefresh}>
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          )}
          {/* All errors handled silently — nothing shown to user */}
          <iframe
            key={refreshKey}
            ref={iframeRef}
            srcDoc={srcDoc}
            className={styles.iframe}
            title="Live Preview"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-downloads"
            referrerPolicy="no-referrer-when-downgrade"
            onLoad={handleSrcDocIframeLoad}
            onError={handleIframeError}
          />
        </div>
      );
    }

    // esbuild building but no output yet
    if (requiresBundling && isBuilding) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <Loader2 size={48} className={styles.buildingSpinner} />
            <div>Building preview…</div>
            <div className={styles.subtext}>
              Preparing your{' '}
              {detectedWebFramework !== 'vanilla' ? safeCap(detectedWebFramework) + ' ' : ''}
              project
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsBuilding(false);
                handleRefresh();
              }}
              style={{ marginTop: '16px', opacity: 0.6 }}
            >
              <RefreshCw size={14} />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    // esbuild errors with no output
    if (requiresBundling && buildErrors.length > 0 && !srcDoc) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            <Wrench size={48} />
            <div>Building your project…</div>
            <div className={styles.subtext}>Review the build errors and retry preview</div>
            {onTryFix && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onTryFix(buildErrors.join('\n'))}
                style={{ marginTop: '12px' }}
              >
                <Wrench size={14} />
                Ask AI to Fix
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              style={{ marginTop: '12px' }}
            >
              <RefreshCw size={14} />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    // No content to preview
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyMessage}>
          <AlertCircle size={48} />
          <div>No preview available</div>
          <div className={styles.subtext}>
            Create an HTML file or publish your project to see the live preview.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      className={`${styles.panel} live-panel ${className || ''} ${isFullscreen ? styles.fullscreen : ''}`}
    >
      <LivePreviewToolbar
        deviceSize={deviceSize}
        onDeviceSizeChange={setDeviceSize}
        onRefresh={handleRefresh}
        onScreenshot={onScreenshot ? handleScreenshot : undefined}
        onAnnotate={onAnnotate ? handleAnnotate : undefined}
        onInspect={srcDoc || effectivePreviewUrl || webContainerUrl ? handleToggleInspect : undefined}
        isInspectMode={effectiveInspectMode}
        onPublish={srcDoc || effectivePreviewUrl || webContainerUrl ? handlePublish : undefined}
        onOpenInNewTab={
          vibesPreviewUrl
            ? () => window.open(vibesPreviewUrl, '_blank', 'noopener,noreferrer')
            : undefined
        }
        onToggleFullscreen={handleToggleFullscreen}
        previewUrl={effectivePreviewUrl}
        isFullscreen={isFullscreen}
        isLoading={!!effectivePreviewUrl && iframeLoading}
        showDeviceControls={true}
        showTerminalToggle={!!webContainerUrl}
        onToggleTerminal={() => setActivePreviewTab((t) => t === 'terminal' ? 'preview' : 'terminal')}
        showInstallButton={false}
        showStopButton={!!webContainerUrl}
        onStopServer={handleStopServer}
        onRestartServer={handleRestartServer}
        isTerminalVisible={activePreviewTab === 'terminal'}
        showAutoPublishToggle={isRustWasmSpa}
        autoPublishEnabled={autoPublishEnabled}
        onToggleAutoPublish={() => setAutoPublishEnabled((v) => !v)}
        currentPath={currentPage}
        availablePages={htmlPages}
        showRoutesDropdown={true}
        onNavigate={(path) => {
          const normalized = path && path.length ? (path.startsWith('/') ? path : `/${path}`) : '/';
          setInternalSelectedPage(normalized);
          if (onPageChange) onPageChange(normalized);
          // For bundled (framework) projects, send postMessage to navigate inside the SPA
          if (requiresBundling && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: 'navigate', path: normalized },
              '*',
            );
          }
        }}
        sessionName={sessionName}
      />

      {/* Preview / Assets tab bar */}
      {(() => {
        return (
          <>
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--border)',
                background: 'var(--card)',
                flexShrink: 0,
                minHeight: 0,
              }}
            >
              <button
                onClick={() => setActivePreviewTab('preview')}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: activePreviewTab === 'preview' ? 600 : 400,
                  color:
                    activePreviewTab === 'preview' ? 'var(--primary)' : 'var(--muted-foreground)',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activePreviewTab === 'preview'
                      ? '2px solid var(--primary)'
                      : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s',
                }}
              >
                Preview
              </button>
              <button
                onClick={() => setActivePreviewTab('assets')}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: activePreviewTab === 'assets' ? 600 : 400,
                  color:
                    activePreviewTab === 'assets' ? 'var(--primary)' : 'var(--muted-foreground)',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activePreviewTab === 'assets'
                      ? '2px solid var(--primary)'
                      : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <ImageIcon size={12} />
                Assets
                {imageFiles.length > 0 && (
                  <span
                    style={{
                      background: 'var(--primary)',
                      color: 'var(--primary-foreground)',
                      borderRadius: '999px',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '0 5px',
                      lineHeight: '16px',
                      minWidth: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {imageFiles.length}
                  </span>
                )}
              </button>
              {webContainerUrl && (
                <button
                  onClick={() => setActivePreviewTab('terminal')}
                  style={{
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: activePreviewTab === 'terminal' ? 600 : 400,
                    color:
                      activePreviewTab === 'terminal' ? 'var(--primary)' : 'var(--muted-foreground)',
                    background: 'none',
                    border: 'none',
                    borderBottom:
                      activePreviewTab === 'terminal'
                        ? '2px solid var(--primary)'
                        : '2px solid transparent',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <TerminalIcon size={12} />
                  Terminal
                </button>
              )}
            </div>

            {/* Terminal tab content */}
            {activePreviewTab === 'terminal' && webContainerUrl && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <LivePreviewTerminal
                  output={terminalOutput}
                  activityLog={aiActivityLog}
                  filter={terminalFilter}
                  onFilterChange={setTerminalFilter}
                  onClear={() => { setTerminalOutput([]); setAiActivityLog([]); }}
                  className=""
                />
                <div style={{ display: 'flex', gap: 4, padding: '4px 8px', background: 'var(--card)', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                  <input
                    type="text"
                    value={terminalCmd}
                    onChange={(e) => setTerminalCmd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTerminalCommand(); }}
                    placeholder="npm install <package>, yarn add, pnpm add..."
                    disabled={terminalRunning}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      background: 'var(--background)',
                      color: 'var(--foreground)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleTerminalCommand}
                    disabled={terminalRunning || !terminalCmd.trim()}
                    style={{
                      padding: '6px 14px',
                      fontSize: '13px',
                      fontWeight: 500,
                      background: terminalRunning ? 'var(--muted)' : 'var(--primary)',
                      color: terminalRunning ? 'var(--muted-foreground)' : 'var(--primary-foreground)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: terminalRunning ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {terminalRunning ? 'Running...' : 'Run'}
                  </button>
                </div>
              </div>
            )}

            {/* Asset gallery panel */}
            {activePreviewTab === 'assets' && (
              <div
                style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '16px',
                  background: 'var(--background)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {imageFiles.length === 0 ? (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--muted-foreground)',
                      gap: '10px',
                      padding: '48px 16px',
                      textAlign: 'center',
                    }}
                  >
                    <ImageIcon size={40} style={{ opacity: 0.3 }} />
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>No images yet</div>
                    <div style={{ fontSize: '12px', maxWidth: '220px' }}>
                      Upload an image in chat with "Use in project" checked, or ask the AI to
                      generate one.
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: '11px',
                        color: 'var(--muted-foreground)',
                        marginBottom: '2px',
                      }}
                    >
                      {imageFiles.length} image{imageFiles.length !== 1 ? 's' : ''} in project
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: '10px',
                      }}
                    >
                      {imageFiles.map((f) => {
                        const isBase64 =
                          f.content?.startsWith('data:image/') ||
                          f.content?.startsWith('data:application/');
                        const isSvg = f.fileName.toLowerCase().endsWith('.svg');
                        const imgSrc = isBase64
                          ? f.content
                          : isSvg
                            ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(f.content || '')}`
                            : null;
                        const filePath = (f.filePath || '/').replace(/\/$/, '') + '/' + f.fileName;
                        return (
                          <div
                            key={f.id}
                            onClick={() => setAssetLightbox(f)}
                            style={{
                              background: 'var(--card)',
                              border: '1px solid var(--border)',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLDivElement).style.borderColor =
                                'var(--primary)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLDivElement).style.borderColor =
                                'var(--border)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                            }}
                          >
                            <div
                              style={{
                                height: '90px',
                                background: 'var(--muted)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}
                            >
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={f.fileName}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ImageIcon size={28} style={{ opacity: 0.3 }} />
                              )}
                            </div>
                            <div style={{ padding: '6px 8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div
                                  style={{
                                    flex: 1,
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: 'var(--foreground)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {f.fileName}
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadAsset(f);
                                  }}
                                  title={`Download ${f.fileName}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border)',
                                    background: 'var(--background)',
                                    color: 'var(--muted-foreground)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Download size={12} />
                                </button>
                              </div>
                              <div
                                style={{
                                  fontSize: '10px',
                                  color: 'var(--muted-foreground)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {filePath}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Asset lightbox */}
            {assetLightbox &&
              (() => {
                const f = assetLightbox;
                const isBase64 =
                  f.content?.startsWith('data:image/') ||
                  f.content?.startsWith('data:application/');
                const isSvg = f.fileName.toLowerCase().endsWith('.svg');
                const imgSrc = isBase64
                  ? f.content
                  : isSvg
                    ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(f.content || '')}`
                    : null;
                const filePath = (f.filePath || '/').replace(/\/$/, '') + '/' + f.fileName;
                return (
                  <div
                    onClick={() => setAssetLightbox(null)}
                    style={{
                      position: 'fixed',
                      inset: 0,
                      background: 'rgba(0,0,0,0.8)',
                      backdropFilter: 'blur(6px)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                    }}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: 'var(--card)',
                        borderRadius: '14px',
                        overflow: 'hidden',
                        maxWidth: 'min(92vw, 960px)',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 16px',
                          borderBottom: '1px solid var(--border)',
                          gap: '12px',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: 'var(--foreground)',
                            }}
                          >
                            {f.fileName}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--muted-foreground)' }}>
                            {filePath}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleDownloadAsset(f)}
                            title={`Download ${f.fileName}`}
                            style={{
                              background: 'none',
                              border: '1px solid var(--border)',
                              cursor: 'pointer',
                              color: 'var(--muted-foreground)',
                              padding: '6px',
                              borderRadius: '8px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => setAssetLightbox(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--muted-foreground)',
                              padding: '4px',
                              borderRadius: '6px',
                            }}
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          overflow: 'auto',
                          padding: '24px',
                          background: 'var(--muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={f.fileName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: 'calc(90vh - 120px)',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              color: 'var(--muted-foreground)',
                              textAlign: 'center',
                              padding: '40px',
                            }}
                          >
                            <ImageIcon size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                            <div>Preview not available for this file type</div>
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                              Path: {filePath}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
          </>
        );
      })()}

      <div
        className={`${styles.panel__body} ${styles.livePanel__body}`}
        style={{ display: activePreviewTab === 'preview' ? undefined : 'none' }}
      >
        <div className={styles.previewArea} ref={previewAreaRef}>
          {effectiveInspectMode && (
            <div className={styles.inspectBanner}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
              Click any element to edit with AI · ESC to cancel
            </div>
          )}
          {elementChange && (
            <div
              style={{
                position: 'absolute',
                bottom: '24px',
                left: '24px',
                right: '24px',
                background: 'var(--muted-foreground)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                zIndex: 1000,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              }}
            >
              <div style={{ flex: 1, fontSize: '13px', color: 'var(--foreground)' }}>
                <strong>Element change pending:</strong>{' '}
                <code>{`<${elementChange.element.tag}>`}</code> (
                {elementChange.element.text?.substring(0, 30) || 'no text'})
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    toast.success('Change accepted');
                    setElementChange(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  ✓ Accept
                </button>
                <button
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent('tasking.elementEditRequest', {
                        detail: {
                          element: elementChange.element,
                          action: elementChange.action,
                          imageData: elementChange.imageData,
                          elementContext: `Re-edit: <${elementChange.element.tag}> element — try a different approach`,
                          sessionId,
                        },
                      }),
                    );
                    toast.info('Re-sending to AI...');
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  ↻ Redo
                </button>
                <button
                  onClick={async () => {
                    if (elementChange.originalFile && elementChange.originalContent) {
                      try {
                        await updateFileMutation.mutateAsync({
                          id: elementChange.originalFile.id,
                          content: elementChange.originalContent,
                        });
                        toast.success('Change reverted');
                      } catch {
                        toast.error('Failed to revert');
                      }
                    }
                    setElementChange(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  ↶ Undo
                </button>
              </div>
            </div>
          )}
          {renderPreviewContent()}
          {/* Inline annotation overlay — snipping tool inside iframe */}
          {isAnnotatingInline && annotationScreenshot && (
            <div className={styles.annotationOverlay}>
              <AnnotationCanvas
                imageUrl={annotationScreenshot}
                sessionId={sessionId}
                onSave={handleInlineAnnotationSave}
                onCancel={handleInlineAnnotationCancel}
              />
            </div>
          )}
        </div>

      </div>

      <PublishSiteDialog
        open={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        sessionId={sessionId}
        sessionFiles={sessionFiles}
        requiresBundling={requiresBundling}
      />
    </div>
  );
};
